import React, { useEffect, useMemo } from "react";
import useVM from "@src/hooks/useVM";
import { IReactionDisposer, makeAutoObservable, observable, reaction, runInAction } from "mobx";
import { RootStore, useStores } from "@stores";
import * as fieldsService from "@services/fieldsService";
import { importBoundaryFeatures } from "@src/services/boundaryFileService";
import {
  listCustomLayers,
  getCustomLayer,
  deleteCustomLayer,
  renameCustomLayer,
  type TCustomLayer,
} from "@src/services/customLayersService";
import { moveFieldsToFarm } from "@src/services/farmsService";
import { syncYieldToCropwise } from "@services/cropwiseService";
import { track, EVENTS } from "@src/utils/analytics";
import type { Feature as GeoJsonFeature, Polygon as GeoJsonPolygon } from "geojson";
import {
  TCrop,
  TFieldStats,
  TLegendItem,
  TStatsItem,
  TSortBy,
  TSortOrder,
  TYieldScale,
  TCameraStats,
  TFieldHarvest,
  TNdviDateItem,
  TProductivityLegendItem,
  TProductivityStats,
  TProductivityJob,
  TFieldTrackDay,
  TFieldTrackDevice,
} from "@services/fieldsService";
import {
  buildFieldTrackFeatures,
  dateSequenceColor,
  installTrackClip,
  isTrackFilterActive,
  resolveTrackMetric,
  TTrackColorMode,
  TTrackFilter,
  TTrackGroup,
  TTrackMetric,
} from "@screens/FieldsScreen/fieldTracksLayer";
import { deviceColorAt } from "@screens/TracksScreen/utils";
import { hiddenSensorRanges } from "@screens/TracksScreen/trackFilter";
import Vector from "ol/source/Vector";
import VectorSource from "ol/source/Vector";
import ImageLayer from "ol/layer/Image";
import ImageStatic from "ol/source/ImageStatic";
import VectorLayer from "ol/layer/Vector";
import OpenLayersMap from "ol/Map";
import View from "ol/View";
import { Draw, Modify } from "ol/interaction";
import { createBox } from "ol/interaction/Draw";
import { doubleClick } from "ol/events/condition";
import { Geometry, LineString, Point } from "ol/geom";
import { Fill, Stroke, Style, Circle as CircleStyle, Text as OlText } from "ol/style";
import { Coordinate } from "ol/coordinate";
import { Feature } from "ol";
import updateFeatureStyle, { colorByLegend } from "@screens/FieldsScreen/fieldsScreenUtils";
import { sortFieldFeatures } from "@screens/FieldsScreen/fieldSort";
import GeoJSON from "ol/format/GeoJSON";
import { getCenter, isEmpty as isEmptyExtent } from "ol/extent";
import { unByKey } from "ol/Observable";
import { EventsKey } from "ol/events";
import { CompareVM } from "@screens/FieldsScreen/CompareVM";
import { LinkedCursorLayer } from "@screens/FieldsScreen/linkedCursorLayer";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import Polygon from "ol/geom/Polygon";
import { toast } from "react-toastify";
import localizations from "@src/localizations";
import { getArea } from "ol/sphere";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { rgb } from "d3-color";
import { convertYield } from "@src/utils/convert";
import { trunc } from "@src/utils/BN";
import { computeTrialRectangle } from "@src/utils/trialGeometry";
import { TFieldTrial } from "@services/fieldsService";
import { transform } from "ol/proj";
import {
  CAMERA_METRIC_BY_VIEW,
  GEOJSON_OPTS,
  customLayerIdOf,
  isCameraView,
  isCustomView,
  isSpectralView,
  type TFieldMapView,
} from "@screens/FieldsScreen/fieldLayers";
import {
  createLowResBaseLayer,
  createMapboxFallbackLayer,
  createSatelliteLayer,
} from "@screens/FieldsScreen/baseLayers";
import {
  assembleTrackGroups,
  changedTrackDays,
  devicesForLocalDay,
  localDayIndex,
  splitBatchByDay,
} from "@screens/FieldsScreen/fieldTracksCache";
import { TrackTimeFocus } from "@screens/FieldsScreen/fieldTrackTime";

const ctx = React.createContext<FieldsScreenVM | null>(null);

export const FieldsScreenVMProvider: React.FC = ({ children }) => {
  const navigate = useNavigate();
  const rootStore = useStores();
  const store = useMemo(() => {
    return new FieldsScreenVM(rootStore, navigate);
  }, [rootStore, navigate]);
  // The VM registers global reactions, timers and an OpenLayers map. Without
  // this pairing every visit to /fields left the previous VM alive — reacting
  // to season/farm changes and holding its whole map (the memory pattern
  // behind the iOS Safari tab kills).
  useEffect(() => {
    store.start();
    return () => store.dispose();
  }, [store]);
  return <ctx.Provider value={store}>{children}</ctx.Provider>;
};

export const useFieldsScreenVM = () => useVM(ctx);

const style = [
  new Style({
    stroke: new Stroke({
      color: "#F6FF8F",
      width: 3,
    }),
    fill: new Fill({
      color: "rgba(246,255,143,0.5)",
    }),
  }),
];

export const optOptions = GEOJSON_OPTS;

type TLegend = Record<string, Array<TLegendItem>>;
type TStatistics = Record<string, TStatsItem>;
type TCameraStatistics = Record<string, TCameraStats>;

export type TOverallMapView = "crop" | "avgYield";

// The view union, predicates and camera metric map live in fieldLayers.ts,
// shared with CompareVM.
export type { TFieldMapView };

/**
 * How a map view came to be on screen. `user` is an explicit dropdown choice;
 * `auto` is a programmatic switch (fallbacks, season resets); `field_change` is
 * the sticky layer being re-rendered for a newly selected field. Filtering to
 * `user` reproduces the original dropdown-only metric (data before
 * 2026-08-19 22:04 UTC, which carries no `schema` property).
 *
 * schema 2 — VM-level tracking, but one de-dupe cursor shared across scopes,
 *            which over-counted `auto` slightly. Live for one day only.
 * schema 3 — per-scope cursors and a `field_id` on every event.
 */
export type TMapViewTrigger = "user" | "auto" | "field_change";


// 8-step red→green palette (matches backend HEATMAP_COLORS subset)
const OVERALL_YIELD_COLORS = [
  "#A50026",
  "#D73027",
  "#F46D43",
  "#FDAE61",
  "#D9EF8B",
  "#A6D96A",
  "#66BD63",
  "#006837",
];

const NO_YIELD_FILL = "rgba(180,180,180,0.4)";
const NO_YIELD_STROKE = "#B4B4B4";

export type TOverallYieldLegendItem = {
  start: number;
  end: number;
  color: string;
  count: number;
};

/**
 * A single heatmap cell the user clicked, pinned so its exact ground location
 * can be opened/shared in Google Maps. All values are derived client-side from
 * the cell's already-loaded geometry — no backend call.
 */
export type TPinnedCell = {
  /** Cell centre in the view projection (EPSG:3857), for Overlay positioning. */
  coord3857: [number, number];
  /** WGS84 centre for the Google Maps link + coordinate readout. */
  lat: number;
  lng: number;
  /** Pre-formatted value string (yield or camera stats), built at click time. */
  label: string;
};

/**
 * One map the yield-evening panel can show: "stored" (no param — what is
 * saved, the applied evening included), "none" (without the evening) or
 * "machine" (the by-machine preview).
 */
type TYieldEvenKey = "stored" | fieldsService.TEveningParam;

/** How often the panel asks how far reading the season's machine tracks is. */
const YIELD_EVEN_PREPARE_POLL_MS = 5000;

/** Whether a yield response is the evening view that was asked for. */
const isEveningView = (key: TYieldEvenKey, response: fieldsService.THeatmapResponse): boolean => {
  // The empty fallback of a failed fetch (and an older backend) has no block.
  const evening = response.evening;
  if (!evening) return false;
  if (key === "stored") return evening.view === "stored";
  if (key === "none") return evening.view === "original";
  return evening.view === "preview" && evening.preview?.strength === key;
};

function computeOverallYieldLegend(
  fields: Feature<Geometry>[]
): TOverallYieldLegendItem[] {
  const yields = fields
    .map((f) => {
      const harvest = f.get("harvest") as TFieldHarvest | null;
      const avgYield = f.get("avg_yield") as number;
      if (!harvest || harvest.harvestPercentage < 0.03 || avgYield <= 0) return null;
      return avgYield;
    })
    .filter((v): v is number => v != null);

  if (yields.length === 0) return [];

  const vmin = Math.min(...yields);
  const vmax = Math.max(...yields);
  const range = vmax - vmin || 1;
  const numBuckets = OVERALL_YIELD_COLORS.length;
  const bucketSize = range / numBuckets;

  const bucketCounts = new Array(numBuckets).fill(0);
  for (const y of yields) {
    const idx = Math.min(
      Math.floor((y - vmin) / bucketSize),
      numBuckets - 1
    );
    bucketCounts[idx]++;
  }

  return OVERALL_YIELD_COLORS.map((color, i) => ({
    start: vmin + i * bucketSize,
    end: i === numBuckets - 1 ? vmax : vmin + (i + 1) * bucketSize,
    color,
    count: bucketCounts[i],
  }));
}

function getYieldColor(avgYield: number, legend: TOverallYieldLegendItem[]): string {
  if (legend.length === 0) return NO_YIELD_FILL;
  const vmin = legend[0].start;
  const vmax = legend[legend.length - 1].end;
  const range = vmax - vmin || 1;
  const bucketSize = range / legend.length;
  const idx = Math.min(
    Math.floor((avgYield - vmin) / bucketSize),
    legend.length - 1
  );
  return legend[idx].color;
}

class FieldsScreenVM {
  drawSource = new VectorSource({ wrapX: false });
  drawInteraction = new Draw({ source: this.drawSource, type: "Polygon" });
  modifyInteraction = new Modify({
    source: this.drawSource,
    deleteCondition: doubleClick,
  });
  drawLayer = new VectorLayer({ source: this.drawSource, style });

  heatmapSource = new Vector({ format: new GeoJSON() });
  fieldsSource = new Vector({ format: new GeoJSON() });

  productivitySource = new Vector({ format: new GeoJSON() });

  tracksSource = new Vector({ format: new GeoJSON() });

  fieldsLayer = new VectorLayer({ source: this.fieldsSource, style });
  heatmapLayer = new VectorLayer({
    source: this.heatmapSource,
    style,
  });
  tracksLayer = new VectorLayer({ source: this.tracksSource, style });
  productivityLayer = new VectorLayer({
    source: this.productivitySource,
    style,
  });
  // Base imagery shared in shape with the compare pane (baseLayers.ts).
  satelliteLayer = createSatelliteLayer();
  mapboxFallbackLayer = createMapboxFallbackLayer();
  maptileLayer = createLowResBaseLayer();

  /** Ghost of the COMPARE pane's pointer, drawn on this map. */
  linkedCursor = new LinkedCursorLayer();

  map = new OpenLayersMap({
    layers: [
      this.satelliteLayer,
      this.mapboxFallbackLayer,
      this.maptileLayer,
      this.fieldsLayer,
      // this.heatmapLayer,
      this.linkedCursor.layer,
    ],
    controls: [],
    view: new View({ maxZoom: 22 }),
  });

  //======================================================================================
  private setupVector = (
    vector: Vector<Geometry>,
    data: any
  ): Array<Feature<Geometry>> => {
    const format = vector.getFormat() ?? new GeoJSON();
    const features = format.readFeatures(data, optOptions) as Array<Feature<Geometry>>;
    features.forEach((feature) => {
      feature.setId(feature.get("id"));
      updateFeatureStyle(feature, { border: true });
    });
    vector.clear(true);
    vector.addFeatures(features);
    return features;
  };

  private setDefaultCenter = () => {
    const defaultMapCenter = [1644365.6088704763, 6262014.14719232];
    const defaultMapZoom = 5;
    const coordinates = this.fields.reduce((acc, feature) => {
      const extent = feature.getGeometry()?.getExtent();
      if (extent != null && !isEmptyExtent(extent)) {
        acc.push(getCenter(extent));
      }
      return acc;
    }, [] as Array<Coordinate>);
    const polygon = new Polygon([coordinates]);
    const view = this.map.getView();
    const combinedExtent = polygon.getExtent();
    if (
      this.fields.length > 0 &&
      coordinates.length > 0 &&
      !isEmptyExtent(combinedExtent)
    ) {
      const mapCenter = getCenter(combinedExtent);
      view.setCenter(mapCenter);
      view.fit(polygon, { padding: [50, 50, 50, 50], maxZoom: 1000 });
    } else {
      view.setCenter(defaultMapCenter);
      view.setZoom(defaultMapZoom);
    }
  };
  //======================================================================================

  search: string = "";
  setSearch = (v: string) => (this.search = v);

  sortBy: TSortBy = "name";
  sortOrder: TSortOrder = "asc";
  setSortBy = (v: TSortBy) => {
    this.sortBy = v;
    // Set sensible default order per sort type
    if (v === "name") this.sortOrder = "asc";
    else if (v === "size") this.sortOrder = "asc";
    else if (v === "crop") this.sortOrder = "asc";
    else if (v === "harvestProgress") this.sortOrder = "desc";
    // Sorted here: a chip must not refetch every boundary.
    this.setFields(sortFieldFeatures(this.fields, this.sortBy, this.sortOrder));
  };

  heatmapLegend: TLegend = {};
  private setHeatmapLegend = (legend: TLegend) => (this.heatmapLegend = legend);

  heatmapStatistics: TStatistics = {};
  private setHeatmapStatistics = (stats: TStatistics) =>
    (this.heatmapStatistics = stats);

  /** Per field: which yield map is on screen and the applied evening, if any. */
  heatmapEvening: Record<string, fieldsService.THeatmapEvening> = {};
  private setHeatmapEvening = (evening: Record<string, fieldsService.THeatmapEvening>) =>
    (this.heatmapEvening = evening);

  yieldScale: TYieldScale = "quantize";
  setYieldScale = (v: TYieldScale) => {
    this.yieldScale = v;
    // Reload heatmap with new scale for the selected field
    const id = this.selectedFieldId;
    if (id != null && this.isCameraView(this.selectedFieldView)) {
      void this.loadCameraLayer(String(id));
      return;
    }
    if (id != null && this.selectedFieldView === "yield") {
      if (this.isYieldEvenOpen) {
        // The evening panel owns the map: redraw what it shows at the new scale.
        this.clearYieldEvenCache();
        this.yieldEvenOnScreen = null;
        void this.syncYieldEvenMap();
        return;
      }
      const req = this.beginHeatmapRequest();
      fieldsService
        .getHeatmapData(String(id), this.rootStore.seasonStore.currentSeasonId, v)
        .then((response) => {
          if (!this.isLatestHeatmap(req)) return;
          if (response.failed) this.clearHeatmapFor(String(id));
          else this.renderHeatmapResponse(String(id), response);
        })
        .finally(() => this.endHeatmapRequest(req));
    }
  };

  /**
   * Put one yield-map response on screen: hexes, legend, stats and its evening
   * block. Shared by the stored map, the scale toggle and the evening preview,
   * so all of them feed the same legend and stats card.
   *
   * `scale` colors the hexes on that legend instead of the response's own
   * colors (see colorByLegend) — the evening's side-by-side view does that on
   * both panes.
   */
  private renderHeatmapResponse = (
    id: string,
    response: fieldsService.THeatmapResponse,
    scale?: fieldsService.TLegendItem[]
  ) => {
    // Clear before building the replacement so the old and new hexes are never
    // held at once.
    this.heatmapSource.clear(true);
    const format = this.heatmapSource.getFormat() ?? new GeoJSON();
    const features = format.readFeatures(response.features, optOptions) as Array<Feature<Geometry>>;
    features.forEach((feature) => {
      feature.setId(feature.get("id"));
      if (!scale) updateFeatureStyle(feature);
    });
    // colorByLegend styles the hexes itself.
    const legend = scale ? colorByLegend(features, scale) : response.legend;
    this.heatmapSource.addFeatures(features);
    // The shared source now holds yield features, not camera/custom ones.
    this.cameraLoadedKey = null;
    this.customLoadedKey = null;
    this.setHeatmapLegend({ ...this.heatmapLegend, [id]: legend });
    this.setHeatmapStatistics({ ...this.heatmapStatistics, [id]: response.stats });
    this.setHeatmapEvening({
      ...this.heatmapEvening,
      [id]: fieldsService.heatmapEveningOf(response),
    });
  };

  /**
   * A failed yield-map load: take this field's hexes, legend and stats off
   * screen (the stats card hides without stats) rather than rendering the
   * placeholder's zeros as "0 t". The error toast already went out.
   */
  private clearHeatmapFor = (id: string) => {
    this.heatmapSource.clear(true);
    const { [id]: _legend, ...legend } = this.heatmapLegend;
    const { [id]: _stats, ...stats } = this.heatmapStatistics;
    this.setHeatmapLegend(legend);
    this.setHeatmapStatistics(stats);
  };

  fields: Feature<Geometry>[] = [];
  private setFields = (f: Feature<Geometry>[]) => (this.fields = f);

  isPolygonReady: boolean = false;
  setIsPolygonReady = (v: boolean) => (this.isPolygonReady = v);

  isFieldEdit: boolean = false;
  setIsFieldEdit = (v: boolean) => {
    // Entering draw/edit mode dismisses an open cell card — map clicks now
    // belong to the Draw/Modify interaction (see Map.tsx handleMapClick).
    if (v) this.clearPinnedCell();
    this.isFieldEdit = v;
  };

  loading: boolean = true;
  private setLoading = (v: boolean) => (this.loading = v);

  // The list's progress bars, loaded beside the list (see loadFields) so the
  // list and map never wait for them. True only while there is no progress
  // to show yet for this season and farm — a reload keeps the old bars.
  harvestLoading: boolean = true;
  private setHarvestLoading = (v: boolean) => (this.harvestLoading = v);
  // Not observable (see makeAutoObservable): which loadFields call is the
  // latest, and which season+farm the progress on screen belongs to.
  fieldsReq = 0;
  harvestScope: string | null = null;

  // Per-field map-data loading (heatmap fetch). Kept separate from `loading`
  // so selecting a field doesn't swap the whole FieldsMenu out for the loading
  // placeholder — that remounts ViewFieldsMenu and loses the list scroll
  // position. This only drives the map overlay spinner.
  heatmapLoading: boolean = false;
  private setHeatmapLoading = (v: boolean) => (this.heatmapLoading = v);

  /**
   * Latest request on the shared heatmap source (yield, camera and custom
   * layers all paint into it). Only the latest may paint or clear the spinner:
   * without this, a slow response for the previous field/view/scale landed
   * after the current one and painted the wrong data. Not observable.
   */
  heatmapReq = 0;
  private beginHeatmapRequest = (): number => {
    this.setHeatmapLoading(true);
    return ++this.heatmapReq;
  };
  private isLatestHeatmap = (req: number) => req === this.heatmapReq;
  private endHeatmapRequest = (req: number) => {
    if (this.isLatestHeatmap(req)) this.setHeatmapLoading(false);
  };
  /** Any selection change: drop whatever is still in flight. */
  private invalidateHeatmapRequests = () => {
    this.heatmapReq++;
    this.setHeatmapLoading(false);
  };

  /** Trials for the currently selected field (used by edit panel). */
  get selectedFieldTrials() {
    if (this.selectedFieldId == null) return [];
    return this.trials.filter((t) => t.fieldId === String(this.selectedFieldId));
  }

  private selectedFieldReaction = async () => {
    const id = this.selectedFieldId;
    // A pin belongs to one cell in one field/view — drop it on any change.
    this.clearPinnedCell();
    // Correction zones belong to one field+season in the yield view; a draft
    // selection would otherwise be saved against whatever is selected next.
    if (this.isYieldAdjustEdit) this.exitYieldAdjustEdit();
    // Same for the evening preview. No restore here: the branches below load
    // whatever the new field/view needs, the stored yield map included.
    if (this.isYieldEvenOpen) this.abandonYieldEven();
    this.resetLayers();
    // Requests for the previous field/view may still be in flight; none of
    // them may paint over what is loaded next.
    this.invalidateHeatmapRequests();

    // Clear productivity state whenever we're not showing that layer — and on
    // any change stop the poll: it belongs to the field that started it.
    if (this.selectedFieldView !== "productivity") this.clearProductivityState();
    else this.stopProductivityPoll();

    // Track geometry is heavy to hold; drop it whenever another layer is shown.
    if (this.selectedFieldView !== "tracks") this.clearTrackState();

    // The dropdown lists this field's custom layers, so the list has to be
    // there whatever layer is showing. Deliberately not awaited — it is a small
    // metadata call and must not delay the layer the user actually picked.
    if (id != null) void this.loadCustomLayers(String(id));

    if (this.selectedFieldView === "yield" && id != null) {
      this.clearNdviState();
      await this.loadHeatmapIfNeeded(String(id));
    } else if (this.selectedFieldView === "tracks" && id != null) {
      this.clearNdviState();
      await this.loadFieldTracks(String(id));
    } else if (this.isCameraView(this.selectedFieldView) && id != null) {
      this.clearNdviState();
      if (!this.cameraAvailable) {
        // Field without camera data (or flag off) — camera views make no
        // sense here; fall back to boundaries.
        this.setSelectedFieldView("border", "auto");
        return;
      }
      await this.loadCameraLayer(String(id));
    } else if (this.isSpectralView(this.selectedFieldView) && id != null) {
      if (this.ndviDates.length > 0 && this.spectralFieldId === String(id)) {
        // Same field, only the spectral layer changed → keep the date list and
        // just re-render the overlay (and thumbnails) for the newly-selected layer.
        if (this.ndviSelectedDate) this.loadNdviImage(String(id), this.ndviSelectedDate);
      } else {
        this.clearNdviState();
        this.ndviLoading = true;
        await this.loadNdviDates(String(id));
        this.ndviLoading = false;
      }
    } else if (this.selectedFieldView === "productivity" && id != null) {
      this.clearNdviState();
      await this.loadProductivity(String(id));
    } else if (this.isCustomView(this.selectedFieldView) && id != null) {
      this.clearNdviState();
      const layerId = this.customLayerIdOf(this.selectedFieldView);
      if (layerId) await this.loadCustomLayer(String(id), layerId);
    } else {
      this.clearNdviState();
    }
  };

  private loadHeatmapIfNeeded = async (id: string) => {
    const existing = this.heatmapSource.getFeatureById(id);
    if (existing != null) return;
    const req = this.beginHeatmapRequest();
    try {
      const response = await fieldsService.getHeatmapData(id, this.rootStore.seasonStore.currentSeasonId, this.yieldScale);
      if (!this.isLatestHeatmap(req)) return;
      if (response.failed) {
        this.clearHeatmapFor(id);
        return;
      }
      if (this.isYieldEvenOpen) {
        // The evening panel owns the map. A late stored map may only fill in
        // for its own field while the panel has drawn nothing yet — never
        // replace a preview.
        if (this.yieldEvenOnScreen !== null || id !== this.yieldEvenFieldId) return;
        this.renderHeatmapResponse(id, response);
        this.yieldEvenOnScreen = "stored";
        return;
      }
      this.renderHeatmapResponse(id, response);
    } catch (e) {
      console.warn(e);
    } finally {
      this.endHeatmapRequest(req);
    }
  };

  private resetLayers = () => {
    const layers: any[] = [
      this.satelliteLayer,
      this.mapboxFallbackLayer,
      this.maptileLayer,
      this.isFieldEdit ? this.drawLayer : this.fieldsLayer,
    ];
    if (
      this.selectedFieldView === "yield" ||
      this.isCameraView(this.selectedFieldView) ||
      this.isCustomView(this.selectedFieldView)
    )
      layers.push(this.heatmapLayer);
    if (this.selectedFieldView === "tracks") layers.push(this.tracksLayer);
    if (this.isSpectralView(this.selectedFieldView) && this.ndviLayer.getSource())
      layers.push(this.ndviLayer);
    if (
      this.selectedFieldView === "productivity" &&
      this.productivitySource.getFeatures().length > 0
    )
      layers.push(this.productivityLayer);
    // Always show saved trial rectangles
    layers.push(this.trialRectLayer);
    // Show active trial drawing layer when in edit + drawing mode
    if (this.isFieldEdit && this.isTrialDrawing) {
      layers.push(this.trialDrawLayer);
    }
    // Correction zones are authored on top of the yield hexes, and are only
    // ever visible while the correction tool is open.
    if (this.isYieldAdjustEdit) layers.push(this.adjustLayer);
    // Always on top: the mirrored compare-pane pointer has to stay visible over
    // every layer.
    layers.push(this.linkedCursor.layer);
    this.map.setLayers(layers);
  };

  overallMapView: TOverallMapView = "crop";
  overallYieldLegend: TOverallYieldLegendItem[] = [];

  setOverallMapView = (v: TOverallMapView, trigger: TMapViewTrigger = "user") => {
    this.overallMapView = v;
    this.trackMapView(v, "overall", trigger);
    this.applyOverallMapColors();
  };

  private applyOverallMapColors = () => {
    const settings = this.rootStore.settingsStore;
    if (this.overallMapView === "avgYield") {
      const legend = computeOverallYieldLegend(this.fields);
      this.overallYieldLegend = legend;

      this.fields.forEach((f) => {
        const harvest = f.get("harvest") as TFieldHarvest | null;
        const avgYield = f.get("avg_yield") as number;
        const isHarvested = harvest && harvest.harvestPercentage >= 0.03 && avgYield > 0;

        if (isHarvested) {
          const color = getYieldColor(avgYield, legend);
          const rgbColor = rgb(color);
          rgbColor.opacity = 0.7;
          f.set("fill", rgbColor.toString());
          f.set("stroke", color);
          // Add yield value to name label
          const converted = convertYield(avgYield, settings.weightUnit, settings.areaUnit, {
            poundsPerBushel: settings.poundsPerBushel,
            poundsPerBag: settings.poundsPerBag,
          });
          const originalName = f.get("original_name") ?? f.get("name");
          f.set("original_name", originalName);
          f.set("name", `${originalName} (${trunc(converted)})`);
        } else {
          f.set("fill", NO_YIELD_FILL);
          f.set("stroke", NO_YIELD_STROKE);
          const originalName = f.get("original_name") ?? f.get("name");
          f.set("original_name", originalName);
        }
        updateFeatureStyle(f, {
          selected: f.getId() === this.selectedFieldId,
          border: true,
        });
      });
    } else {
      this.restoreCropView();
      this.fields.forEach((f) => {
        updateFeatureStyle(f, {
          selected: f.getId() === this.selectedFieldId,
          border: true,
        });
      });
    }
    // Replace array reference so MobX triggers re-render of field list
    this.fields = [...this.fields];
  };

  /**
   * Leave the avg-yield overview: crop colours back and the real names back.
   * The overview writes "Name (12.3)" into each feature's `name` (the map
   * label); leaving it there let an edit save that label as the field name.
   * Every exit from the overview must go through here.
   */
  private restoreCropView = () => {
    this.overallMapView = "crop";
    this.overallYieldLegend = [];
    this.fields.forEach((f) => {
      f.set("fill", f.get("crop_fill") ?? "rgba(76, 175, 80, 0.6)");
      f.set("stroke", f.get("crop_stroke") ?? "#4CAF50");
      const originalName = f.get("original_name");
      if (originalName != null) {
        f.set("name", originalName);
        f.unset("original_name");
      }
    });
  };

  selectedFieldView: TFieldMapView = "border";
  setSelectedFieldView = (v: TFieldMapView, trigger: TMapViewTrigger = "user") => {
    this.selectedFieldView = v;
    this.trackMapView(v, "field", trigger);
  }

  // ── Map view analytics ──────────────────────────────────────
  //
  // This event used to fire only from the layer dropdown, which left the most
  // common paths invisible: the chosen layer is sticky across fields, so one
  // dropdown click could be followed by any number of untracked renders. We
  // now emit from the VM wherever the rendered (field, view) pair changes and
  // tag how it happened. The `schema` property versions these semantics — see
  // TMapViewTrigger above; untagged data counted dropdown switches only, so it
  // is not directly comparable.

  // Not observable (see makeAutoObservable overrides) — these are de-dupe
  // cursors for analytics, nothing renders from them. One per scope: a single
  // shared cursor let an `overall` switch clear the per-field key, so the next
  // identical field render re-fired (observed in production 2026-08-20).
  lastTrackedMapView: Record<"field" | "overall", string | null> = {
    field: null,
    overall: null,
  };

  private trackMapView = (
    view: TFieldMapView | TOverallMapView,
    scope: "field" | "overall",
    trigger: TMapViewTrigger
  ) => {
    const fieldId = this.selectedFieldId != null ? String(this.selectedFieldId) : null;
    // Same layer on the same field is one view, however many times the
    // surrounding reactions re-run.
    const key = `${fieldId ?? "none"}:${view}`;
    if (key === this.lastTrackedMapView[scope]) return;
    this.lastTrackedMapView[scope] = key;
    // field_id is what makes the de-dupe auditable after the fact — without it
    // two identical rows are indistinguishable from one field firing twice.
    track(EVENTS.MAP_VIEW_SELECTED, {
      view,
      scope,
      trigger,
      field_id: fieldId,
      schema: 3,
    });
  };

  // ── Machine tracks on the field ─────────────────────────────
  //
  // Two-step load by design: the day summary is cheap (a small table read),
  // the points are not. We fetch the summary first so the day picker renders
  // immediately, then pull points only for the days actually drawn.

  trackDays: TFieldTrackDay[] = [];
  trackDevices: TFieldTrackDevice[] = [];
  trackMetric: TTrackMetric = "sensor";
  trackColorMode: TTrackColorMode = "value";
  /** Null until the user touches the toggle; before that the mode is derived
   *  from how many machines worked the field, matching the Tracks screen. */
  private trackColorModeTouched = false;
  selectedTrackDeviceIds: string[] = [];
  tracksLoading = false;
  tracksTruncated = false;
  /** Days whose points have arrived. Batches land one at a time, so this is
   *  what lets the picker show each day filling in rather than one long wait. */
  loadedTrackDays: string[] = [];
  tracksRefreshing = false;
  tracksComputedThrough: string | null = null;

  // ── Display filters (color / speed) ──
  // Kept across field changes on purpose: a user sweeping fields for low-yield
  // patches wants the same filter on each. Both toggles stay visible in the
  // panel, so a filter is never active without the user seeing it.
  trackColorFilterOn = false;
  /** Sensor values kept while the color filter is on, 0–100 inclusive. */
  trackSensorMin = 0;
  trackSensorMax = 100;
  trackSpeedFilterOn = false;
  /** km/h; null until the filter is first switched on and prefilled. */
  trackSpeedMinKmh: number | null = null;
  trackSpeedMaxKmh: number | null = null;
  /** Hide headland turns (each machine's analytics turn gate). */
  trackRemoveTurns = false;

  /** One day read by time of pass, with a start/stop window. Reads
   *  `trackDevices` so its timeline follows the day's batch landing. */
  trackTime = new TrackTimeFocus((day) => {
    void this.trackDevices;
    return devicesForLocalDay(day, this.trackSourceDays(day), this.trackDayCache);
  });
  /** Colour mode to return to when the focused day is closed. */
  private trackColorModeBeforeFocus: { mode: TTrackColorMode; touched: boolean } | null = null;

  /** Click a worked day: draw only that day, coloured by time of pass, with
   *  the start/stop slider. Clicking it again goes back to every day. */
  focusTrackDay = (day: string) => {
    const wasFocused = this.trackTime.day != null;
    this.trackTime.toggle(day);
    if (this.trackTime.day != null && !wasFocused) {
      this.trackColorModeBeforeFocus = {
        mode: this.trackColorMode,
        touched: this.trackColorModeTouched,
      };
      this.trackColorMode = "date";
      this.trackColorModeTouched = true;
    } else if (this.trackTime.day == null) {
      this.restoreColorModeAfterFocus();
    }
    this.rebuildTrackFeatures();
  };

  private restoreColorModeAfterFocus = () => {
    const before = this.trackColorModeBeforeFocus;
    this.trackColorModeBeforeFocus = null;
    if (!before) return;
    this.trackColorMode = before.mode;
    this.trackColorModeTouched = before.touched;
  };

  setTrackTimeWindow = (from: number, to: number) => {
    this.trackTime.setWindow(from, to);
    this.rebuildTrackFeatures();
  };

  /**
   * Days per request. Every day is always loaded — the point of this view is
   * the whole field's harvest — so batching is what keeps each response small
   * and lets the map fill in progressively instead of blocking on one payload.
   */
  private static readonly DAYS_PER_BATCH = 3;

  /** What the tracks source currently holds — field, season, days, devices and
   *  metric all change the geometry, so all of them key the guard. */
  private tracksLoadedKey: string | null = null;

  /** How far through the worked days this load has got, in the local days
   *  the list shows. */
  get trackLoadProgress(): { loaded: number; total: number; pct: number } {
    const days = this.trackDayList;
    const total = days.length;
    const loaded = days.filter((d) => this.trackSourceDays(d).every((u) => this.loadedTrackDays.includes(u))).length;
    return { loaded, total, pct: total ? Math.round((loaded / total) * 100) : 0 };
  }

  /** A worked (local) day still waiting for any of its batches. */
  isTrackDayPending = (day: string): boolean =>
    this.tracksLoading &&
    this.trackSourceDays(day).some((u) => !this.loadedTrackDays.includes(u));

  /** Local day → the UTC days its points are fetched and cached under. */
  get trackLocalDayIndex(): Map<string, string[]> {
    return localDayIndex(this.trackDays);
  }

  /** The worked days as the user reads them: local (browser) calendar days,
   *  like the Tracks screen. */
  get trackDayList(): string[] {
    return [...this.trackLocalDayIndex.keys()];
  }

  /** The UTC days the backend buckets by — what is fetched and cached. */
  get trackFetchDayList(): string[] {
    return [...new Set(this.trackDays.map((d) => d.day))].sort();
  }

  trackSourceDays = (localDay: string): string[] =>
    this.trackLocalDayIndex.get(localDay) ?? [];

  /**
   * Every machine that worked this field, in a stable order.
   *
   * Ordered by first appearance in the day list, never by current selection:
   * keying colors off `selectedTrackDeviceIds` would recolor every other
   * machine each time the user toggled one off.
   */
  get trackDeviceList(): Array<{ deviceId: string; name: string; color: string }> {
    const names = new Map<string, string>();
    for (const d of this.trackDays) {
      // The day summary carries the real machine name, so the picker reads
      // correctly before any points have been fetched. Falling back to the ESN
      // here is what put raw hex ids in the legend.
      if (!names.has(d.deviceId)) names.set(d.deviceId, d.name || d.deviceId);
    }
    for (const d of this.trackDevices) names.set(d.deviceId, d.name || d.deviceId);
    return [...names.entries()].map(([deviceId, name], i) => ({
      deviceId,
      name,
      color: deviceColorAt(i),
    }));
  }

  /** The one color a machine's swatch and its drawn swaths both use. */
  trackDeviceColor = (deviceId: string): string =>
    this.trackDeviceList.find((d) => d.deviceId === deviceId)?.color ?? "#888888";

  /** Every day this field was worked, oldest first, on a red-to-green ramp so
   *  the legend reads as a sequence: first day worked through to last. */
  get trackDayLegend(): Array<{ day: string; color: string }> {
    const days = this.trackDayList;
    return days.map((day, i) => ({ day, color: dateSequenceColor(i, days.length) }));
  }

  /** The one colour a day's swatch and its drawn swaths both use. */
  trackDayColor = (day: string): string =>
    this.trackDayLegend.find((d) => d.day === day)?.color ?? "#888888";

  setTrackMetric = (m: TTrackMetric) => {
    this.trackMetric = m;
    this.rebuildTrackFeatures();
  };

  /** The filter as the layer and the export see it — a switched-off toggle
   *  contributes nothing, whatever values it remembers. */
  get trackFilter(): TTrackFilter {
    return {
      sensorMin: this.trackColorFilterOn ? this.trackSensorMin : null,
      sensorMax: this.trackColorFilterOn ? this.trackSensorMax : null,
      speedMinKmh: this.trackSpeedFilterOn ? this.trackSpeedMinKmh : null,
      speedMaxKmh: this.trackSpeedFilterOn ? this.trackSpeedMaxKmh : null,
      removeTurns: this.trackRemoveTurns,
    };
  }

  /** Whether anything is hidden right now: a filter, or a machine toggled off.
   *  Drives the "filtered or all?" question on export. */
  get tracksFiltered(): boolean {
    const machineHidden = this.trackDeviceList.some(
      (d) => !this.selectedTrackDeviceIds.includes(d.deviceId)
    );
    return machineHidden || isTrackFilterActive(this.trackFilter, this.trackMetric === "sensor");
  }

  /** The widest admin working-speed band across the machines on this field —
   *  where the speed filter starts. */
  get trackDeviceSpeedBand(): { min: number; max: number } {
    let min = Infinity;
    let max = -Infinity;
    for (const d of this.trackDevices) {
      if (typeof d.minSpeed === "number" && d.minSpeed < min) min = d.minSpeed;
      if (typeof d.maxSpeed === "number" && d.maxSpeed > max) max = d.maxSpeed;
    }
    return { min: isFinite(min) ? min : 1, max: isFinite(max) ? max : 15 };
  }

  setTrackColorFilterOn = (on: boolean) => {
    this.trackColorFilterOn = on;
    this.rebuildTrackFeatures();
  };

  setTrackSensorRange = (min: number, max: number) => {
    this.trackSensorMin = min;
    this.trackSensorMax = max;
    this.rebuildTrackFeatures();
  };

  setTrackSpeedFilterOn = (on: boolean) => {
    this.trackSpeedFilterOn = on;
    if (on && this.trackSpeedMinKmh == null && this.trackSpeedMaxKmh == null) {
      const band = this.trackDeviceSpeedBand;
      this.trackSpeedMinKmh = band.min;
      this.trackSpeedMaxKmh = band.max;
    }
    this.rebuildTrackFeatures();
  };

  setTrackRemoveTurns = (on: boolean) => {
    this.trackRemoveTurns = on;
    this.rebuildTrackFeatures();
  };

  /** Either bound may be cleared (null = unbounded on that side). */
  setTrackSpeedRange = (minKmh: number | null, maxKmh: number | null) => {
    this.trackSpeedMinKmh = minKmh;
    this.trackSpeedMaxKmh = maxKmh;
    this.rebuildTrackFeatures();
  };

  /**
   * The panel's Reload: throw away every cached day and fetch the field's
   * tracks again.
   *
   * Each machine's delay and width arrive with its points, and finished days
   * are cached for good, so a delay changed in device settings never reached
   * this map without a full page reload. Filters are kept; the day and machine
   * state is rebuilt from the fresh response.
   */
  reloadFieldTracks = async () => {
    const id = this.selectedFieldId;
    if (id == null || this.tracksLoading) return;
    this.tracksLoadedKey = null;
    await this.loadFieldTracks(String(id));
  };

  /**
   * Download URL for the season's raw points of the selected field.
   *
   * `onlyFiltered` sends what the preview hides — machines toggled off, the
   * color range, the speed range, headland turns — so the file matches the
   * screen. Without it
   * every machine and every point that passes the device settings is exported.
   */
  trackPointsExportUrl = (format: "csv" | "shp" | "kml", onlyFiltered: boolean): string | null => {
    const id = this.selectedFieldId;
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (id == null || !seasonId) return null;
    const units = this.rootStore.settingsStore.units;
    const filter = this.trackFilter;
    const name = this.selectedField?.get("name");
    return fieldsService.fieldTrackPointsExportUrl(String(id), {
      seasonId,
      format,
      fieldName: typeof name === "string" ? name : null,
      units,
      ...(onlyFiltered
        ? {
            deviceIds: this.selectedTrackDeviceIds,
            hiddenRanges:
              this.trackMetric === "sensor" ? hiddenSensorRanges(filter) : [],
            speedMinKmh: filter.speedMinKmh,
            speedMaxKmh: filter.speedMaxKmh,
            removeTurns: filter.removeTurns,
          }
        : {}),
    });
  };

  setTrackColorMode = (m: TTrackColorMode) => {
    this.trackColorMode = m;
    this.trackColorModeTouched = true;
    this.rebuildTrackFeatures();
  };

  /** Show/hide a machine. Purely a display filter: every machine's points are
   *  already loaded, so this never touches the network. */
  toggleTrackDevice = (deviceId: string) => {
    this.trackSelectionTouched = true;
    this.selectedTrackDeviceIds = this.selectedTrackDeviceIds.includes(deviceId)
      ? this.selectedTrackDeviceIds.filter((d) => d !== deviceId)
      : [...this.selectedTrackDeviceIds, deviceId];
    this.rebuildTrackFeatures();
  };

  private trackRequestSeq = 0;

  private clearTrackState = () => {
    // In-flight batches belong to the state being cleared; their seq check
    // drops them (a season switch used to let them land and cache old days).
    this.trackRequestSeq++;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.tracksRefreshing = false;
    this.trackSelectionTouched = false;
    this.loadedTrackDays = [];
    this.trackDayCache.clear();
    this.trackDays = [];
    this.trackDevices = [];
    this.selectedTrackDeviceIds = [];
    this.tracksTruncated = false;
    this.tracksComputedThrough = null;
    this.tracksLoadedKey = null;
    this.trackColorModeTouched = false;
    this.trackTime.clear();
    this.trackColorModeBeforeFocus = null;
    this.tracksSource.clear(true);
  };

  /**
   * The days that may still be growing: today and yesterday, in UTC.
   *
   * `trackDayCache` is keyed by the UTC day a fix belongs to, and every other
   * day in a season is finished — the machine cannot add to 14 August. Today
   * can, and so can yesterday: a combine working a US evening is already past
   * UTC midnight, so both days are live at once for a negative-offset farm.
   */
  private openDayKeys = (): string[] => {
    const now = Date.now();
    return [now, now - 86_400_000].map((t) =>
      new Date(t).toISOString().slice(0, 10)
    );
  };

  /**
   * Forget the cached points for any day that is not finished yet.
   *
   * The cache has no expiry by design — a finished day never changes, so a
   * toggle over it should never cost a request. Today is the exception, and it
   * was the bug: plot a field, let the machine work another hour, come back,
   * and the ribbon still stopped where the combine was on the first plot,
   * because the day was already in the cache and so never counted as missing.
   *
   * @returns true when something was dropped, i.e. a refetch is worth doing.
   */
  private dropOpenDays = (): boolean => {
    let dropped = false;
    for (const day of this.openDayKeys()) {
      if (this.trackDayCache.delete(day)) dropped = true;
    }
    // loadTrackPoints short-circuits on this key, so an eviction that leaves it
    // in place would evict and then redraw from nothing.
    if (dropped) this.tracksLoadedKey = null;
    return dropped;
  };

  /**
   * Forget the cached points of every day whose summary row changed
   * (changedTrackDays). "A finished day never changes" does not hold for a
   * John Deere combine: its data lands in import batches every few hours, a
   * re-import rewrites the operation's window, and either can hit a day that
   * is already cached. Call it with the new summary BEFORE applySummary, which
   * replaces the rows it compares against.
   *
   * @returns true when something was dropped, i.e. a refetch is worth doing.
   */
  private dropChangedDays = (nextDays: TFieldTrackDay[]): boolean => {
    let dropped = false;
    for (const day of changedTrackDays(this.trackDays, nextDays)) {
      if (this.trackDayCache.delete(day)) dropped = true;
    }
    if (dropped) this.tracksLoadedKey = null;
    return dropped;
  };

  private loadFieldTracks = async (id: string) => {
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (!seasonId) return;
    if (this.tracksLoadedKey?.startsWith(`${id}:${seasonId}:`)) {
      // Same field reopened: the cached days are redrawn immediately, and only
      // the days that can have changed are re-requested — the open days, and
      // any day whose summary row moved (an import landed). Reloading the
      // whole season here instead would refetch hundreds of thousands of
      // points to pick up one afternoon's work. The summary is a small table
      // read, so it is asked every time.
      this.rebuildTrackFeatures();
      this.dropOpenDays();
      const summary = await fieldsService.getFieldTracks(id, seasonId);
      // A failed refresh keeps the days already shown.
      if (summary.failed) return;
      // Another field or season was opened meanwhile: this summary is not its.
      if (
        String(this.selectedFieldId) !== id ||
        this.rootStore.seasonStore.currentSeasonId !== seasonId
      ) return;
      this.dropChangedDays(summary.days);
      runInAction(() => this.applySummary(summary));
      // A no-op when nothing was dropped and the day list is unchanged.
      await this.loadTrackPoints();
      if (summary.refreshing) this.pollWhileRefreshing(id, seasonId);
      return;
    }

    this.clearTrackState();
    this.tracksLoading = true;
    try {
      const summary = await fieldsService.getFieldTracks(id, seasonId);
      // The user may have moved to another field or layer meanwhile.
      if (this.selectedFieldView !== "tracks" || String(this.selectedFieldId) !== id) return;
      runInAction(() => {
        this.applySummary(summary);
      });

      // Draw whatever is already known right away. Waiting for a background
      // rescan to finish first meant up to a minute of blank map with no
      // indicator — the scan only ever ADDS days, so there is no reason to
      // withhold the ones we already have.
      if (summary.days.length > 0) await this.loadTrackPoints();

      // Then keep asking until the scan lands, so newly found days appear.
      if (summary.refreshing) this.pollWhileRefreshing(id, seasonId);
    } catch (e) {
      console.warn(e);
    } finally {
      runInAction(() => (this.tracksLoading = false));
    }
  };

  /** Set as soon as the user hides or shows a machine; from then on a
   *  background refresh must never overwrite their choice. */
  private trackSelectionTouched = false;

  private applySummary = (summary: fieldsService.TFieldTracksResponse) => {
    this.trackDays = summary.days;
    this.tracksComputedThrough = summary.computedThrough;
    this.tracksRefreshing = summary.refreshing;

    // Re-seed only while the user has not made a choice. Re-seeding on every
    // poll used to throw away days they had just ticked; never re-seeding meant
    // days discovered by the background scan were listed but never drawn.
    if (this.trackSelectionTouched) return;
    this.selectedTrackDeviceIds = [
      ...new Set(summary.days.map((d) => d.deviceId)),
    ];
  };

  private pollTimer: ReturnType<typeof setTimeout> | null = null;

  private pollWhileRefreshing = (id: string, seasonId: string, attempt = 0) => {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    // Give up after ~5 minutes rather than polling a stuck scan forever.
    if (attempt > 60) {
      runInAction(() => (this.tracksRefreshing = false));
      return;
    }
    this.pollTimer = setTimeout(async () => {
      // The user may have moved on while we waited.
      if (this.selectedFieldView !== "tracks" || String(this.selectedFieldId) !== id) {
        return;
      }
      try {
        const summary = await fieldsService.getFieldTracks(id, seasonId);
        if (summary.failed) {
          // Never apply the empty placeholder over the days on screen.
          runInAction(() => (this.tracksRefreshing = false));
          return;
        }
        // Read before applySummary overwrites it. A watermark that moved means
        // the scan has seen fixes it had not seen before, so today's cached
        // points are now short — every other poll leaves the cache alone.
        const advanced =
          !!summary.computedThrough &&
          summary.computedThrough !== this.tracksComputedThrough;
        // Same reading for any older day whose row the scan changed (an
        // import or re-import landed on it).
        this.dropChangedDays(summary.days);
        runInAction(() => this.applySummary(summary));
        if (advanced) this.dropOpenDays();
        // loadTrackPoints no-ops when the selection is unchanged, so this is
        // free unless the scan actually found something new.
        void this.loadTrackPoints();
        if (summary.refreshing) {
          this.pollWhileRefreshing(id, seasonId, attempt + 1);
        }
      } catch (e) {
        console.warn(e);
        runInAction(() => (this.tracksRefreshing = false));
      }
    }, 5000);
  };


  /**
   * Points already fetched, keyed by day.
   *
   * Every toggle re-requests the whole selection, so ticking days off and on
   * again re-downloaded everything each time — enough to trip the server's rate
   * limit just by exploring. Caching per day makes a toggle free once its data
   * has been seen. Cleared with the rest of the track state on field/season
   * change; deliberately NOT observable — it holds tens of thousands of rows
   * and nothing renders from it directly, so deep-proxying would be pure
   * overhead. Public only because MobX's annotation map cannot name a private
   * member; treat it as internal.
   */
  trackDayCache = new Map<string, TFieldTrackDevice[]>();

  /** Split a batch response into per-day slices so each day can be cached
   *  independently of the batch it happened to arrive in. */
  private cacheBatch = (batchDays: string[], devices: TFieldTrackDevice[]) => {
    for (const [day, slice] of splitBatchByDay(batchDays, devices)) {
      this.trackDayCache.set(day, slice);
    }
  };

  /**
   * The (day, machine) groups to draw, from whatever is cached.
   *
   * Grouping by day is what lets a feature be coloured by its date, and it
   * removes the cross-day sort that used to be needed: each group's points come
   * from one day's response and are already chronological.
   */
  /** `onlyDays` are UTC (fetch) days. A focused local day draws alone. */
  private assembleGroups = (onlyDays?: string[]): TTrackGroup[] => {
    const focus = this.trackTime.day;
    if (focus) {
      // One run per machine across the UTC days the local day overlaps, so
      // the swath has no seam at UTC midnight (a US evening). A focused day
      // is always rebuilt whole, never appended per batch.
      const visible = new Set(this.selectedTrackDeviceIds);
      return devicesForLocalDay(focus, this.trackSourceDays(focus), this.trackDayCache)
        .filter((device) => visible.has(device.deviceId))
        .map((device) => ({ day: focus, device }));
    }
    return assembleTrackGroups(this.trackFetchDayList, this.trackDayCache, this.selectedTrackDeviceIds, onlyDays);
  };

  private loadTrackPoints = async () => {
    const id = this.selectedFieldId;
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (id == null || !seasonId) return;

    // Every day, always. Machines are filtered at draw time, not fetched
    // selectively, so hiding one never costs a request.
    const days = [...this.trackFetchDayList];
    const key = `${id}:${seasonId}:${days.join(",")}`;
    if (this.tracksLoadedKey === key) return;

    if (days.length === 0) {
      this.tracksLoadedKey = key;
      runInAction(() => {
        this.trackDevices = [];
        this.tracksSource.clear(true);
      });
      return;
    }

    const seq = ++this.trackRequestSeq;
    const missing = days.filter((d) => !this.trackDayCache.has(d));

    // Everything already in hand — redraw without touching the network.
    if (missing.length === 0) {
      this.tracksLoadedKey = key;
      runInAction(() => {
        this.loadedTrackDays = [...days];
        this.trackDevices = [...this.trackDayCache.values()].flat();
        this.applyAutoColorMode();
        this.ensureMetricAvailable();
        this.rebuildTrackFeatures();
      });
      return;
    }

    this.tracksLoading = true;
    runInAction(() => {
      this.loadedTrackDays = days.filter((d) => this.trackDayCache.has(d));
      // Redraw what is cached once, against the current day list (a day the
      // scan just found shifts every date colour), then let each batch append
      // only its own days below.
      this.rebuildTrackFeatures();
    });

    // Newest first so the most recent passes appear while older ones load.
    const batches: string[][] = [];
    const ordered = [...missing].sort().reverse();
    for (let i = 0; i < ordered.length; i += FieldsScreenVM.DAYS_PER_BATCH) {
      batches.push(ordered.slice(i, i + FieldsScreenVM.DAYS_PER_BATCH));
    }

    let truncated = false;
    let failed = false;
    try {
      for (const batch of batches) {
        const data = await fieldsService.getFieldTracks(String(id), seasonId, batch);
        // A newer selection started while this one was in flight — drop it
        // rather than painting stale days over the new ones.
        if (seq !== this.trackRequestSeq) return;
        if (data.failed) {
          // Not "no points on these days": leave them uncached and the
          // selection unmarked, so reopening the field asks again.
          failed = true;
          break;
        }

        this.cacheBatch(batch, data.devices);
        if (data.truncated) truncated = true;
        const truncatedSoFar = truncated;

        runInAction(() => {
          this.loadedTrackDays = [...this.loadedTrackDays, ...batch];
          this.trackDevices = [...this.trackDayCache.values()].flat();
          this.tracksTruncated = truncatedSoFar;
          this.tracksComputedThrough = data.computedThrough;
          this.tracksRefreshing = data.refreshing;
          const mode = this.trackColorMode;
          const metric = this.trackMetric;
          this.applyAutoColorMode();
          this.ensureMetricAvailable();
          // Only a changed metric or colour mode invalidates what is drawn —
          // or a focused day, whose time colours span every batch it needs.
          if (
            mode !== this.trackColorMode ||
            metric !== this.trackMetric ||
            this.trackTime.day != null
          ) {
            this.rebuildTrackFeatures();
          } else {
            this.appendTrackFeatures(batch);
          }
        });
      }
      if (seq === this.trackRequestSeq && !failed) this.tracksLoadedKey = key;
    } catch (e) {
      console.warn(e);
    } finally {
      if (seq === this.trackRequestSeq) {
        runInAction(() => (this.tracksLoading = false));
      }
    }
  };

  /** One machine reads better coloured by value; several need telling apart.
   *  Derived from the day summary, never from the fetched devices — that list
   *  shrinks under truncation, which made the toggle flip on its own. */
  private applyAutoColorMode = () => {
    if (this.trackColorModeTouched) return;
    this.trackColorMode =
      new Set(this.trackDays.map((d) => d.deviceId)).size > 1 ? "device" : "value";
  };

  /** Drop back to the sensor metric when the machines now on screen cannot
   *  serve the selected one — see resolveTrackMetric. */
  private ensureMetricAvailable = () => {
    this.trackMetric = resolveTrackMetric(this.trackMetric, this.trackDevices);
  };

  /**
   * Clip the tracks layer to the selected field's boundary, on the canvas.
   *
   * The alternative — clipping the points server-side — looks tidier but
   * misdraws: a swath would stop short of the boundary, and worse, two fixes
   * either side of a headland turn taken outside the boundary would be bridged
   * into a ribbon across ground that was never harvested. Drawing the full
   * pass and masking it here gets the yield-map look with none of that.
   */

  /** Re-derive the drawn geometry from the already-fetched points. Metric and
   *  color-mode changes go through here — they never refetch. */
  private rebuildTrackFeatures = () => {
    // Clear BEFORE building: the old feature set can then be collected while
    // the new one is built, instead of both being alive at the peak.
    this.tracksSource.clear(true);
    this.appendTrackFeatures();
  };

  /** Draw the given days (default: all) on top of what is already drawn.
   *  Each (day, machine) group's geometry and colour depend only on that
   *  group, the metric and the colour mode, so appending a batch draws exactly
   *  what a full rebuild would — without rebuilding every earlier day again. */
  private appendTrackFeatures = (onlyDays?: string[]) => {
    const { features } = buildFieldTrackFeatures({
      groups: this.assembleGroups(onlyDays),
      metric: this.trackMetric,
      colorMode: this.trackColorMode,
      colorForDevice: this.trackDeviceColor,
      colorForDay: this.trackDayColor,
      filter: this.trackFilter,
      time: this.trackTime.render,
    });
    this.tracksSource.addFeatures(features);
  };

  // ── Potato-camera layers (count / stone % / mean size) ──────
  isCameraView = isCameraView;

  /** Camera UI gate: admin flag on the account + uploaded data on the field. */
  get cameraAvailable(): boolean {
    if (!this.rootStore.accountStore.cameraInstalled) return false;
    return Boolean(this.selectedField?.get("has_camera_data"));
  }

  // ── Custom layers (customer-supplied maps) ──────────────────
  // Unlike every other layer, these are dynamic: a field can hold several and
  // the user names them, so the view mode carries the layer id rather than
  // being a fixed literal. They render through the same heatmap source as
  // yield/camera — the colours arrive on the features, so nothing else changes.
  isCustomView = isCustomView;
  customLayerIdOf = customLayerIdOf;

  customLayers: TCustomLayer[] = [];
  private setCustomLayers = (layers: TCustomLayer[]) => (this.customLayers = layers);

  /** The custom layer currently on screen, for the legend and panel. */
  get activeCustomLayer(): TCustomLayer | null {
    const layerId = this.customLayerIdOf(this.selectedFieldView);
    if (!layerId) return null;
    return this.customLayers.find((l) => l.id === layerId) ?? null;
  }

  private customLoadedKey: string | null = null;
  /** Which field `customLayers` was fetched for, so we refetch on field change. */
  private customLayersFieldId: string | null = null;

  loadCustomLayers = async (id: string, force = false) => {
    if (!force && this.customLayersFieldId === id) return;
    const layers = await listCustomLayers(id);
    runInAction(() => {
      this.customLayersFieldId = id;
      this.setCustomLayers(layers);
    });
  };

  private loadCustomLayer = async (id: string, layerId: string) => {
    const key = `${id}:${layerId}`;
    if (this.customLoadedKey === key && this.heatmapSource.getFeatures().length > 0) return;
    const req = this.beginHeatmapRequest();
    try {
      const response = await getCustomLayer(id, layerId);
      if (!this.isLatestHeatmap(req)) return;
      const format = this.heatmapSource.getFormat() ?? new GeoJSON();
      const features = format.readFeatures(response.features, optOptions) as Array<Feature<Geometry>>;
      features.forEach((feature) => {
        feature.setId(feature.get("id"));
        updateFeatureStyle(feature);
      });
      this.heatmapSource.clear(true);
      this.heatmapSource.addFeatures(features);
      this.customLoadedKey = key;
      // The shared source now holds custom features, not yield/camera ones.
      this.cameraLoadedKey = null;
      const legend = { ...this.heatmapLegend };
      legend[id] = response.legend;
      this.setHeatmapLegend(legend);
    } catch (e) {
      console.warn(e);
      // The layer may have been deleted in another tab — fall back rather than
      // leaving the map on a view that can never load. Only if the user is
      // still looking at it.
      if (this.isLatestHeatmap(req)) {
        runInAction(() => this.setSelectedFieldView("border", "auto"));
      }
    } finally {
      this.endHeatmapRequest(req);
    }
  };

  /**
   * The wizard is opened from the layer dropdown — picking "add" is an action,
   * not a view — so its visibility lives on the VM and the dialog is mounted
   * once in Map.tsx rather than inside the dropdown.
   *
   * `target` records which pane asked, so a layer added from the compare pane
   * appears there instead of hijacking the left map.
   */
  customLayerDialogVisible = false;
  customLayerDialogTarget: "main" | "compare" = "main";
  openCustomLayerDialog = (target: "main" | "compare" = "main") => {
    this.customLayerDialogTarget = target;
    this.customLayerDialogVisible = true;
  };
  closeCustomLayerDialog = () => (this.customLayerDialogVisible = false);

  /** Called after the wizard imports a layer, to show it immediately. */
  onCustomLayerAdded = async (fieldId: string, layerId: string) => {
    // Both panes list the same field's layers, so both need refreshing however
    // the wizard was opened.
    await this.loadCustomLayers(fieldId, true);
    const cvm = this.compareVM;
    if (cvm) await cvm.loadCustomLayers();
    if (this.customLayerDialogTarget === "compare" && cvm) {
      cvm.setViewMode(`custom:${layerId}`);
    } else {
      this.setSelectedFieldView(`custom:${layerId}`, "user");
    }
  };

  renameCustomLayer = async (fieldId: string, layerId: string, name: string) => {
    await renameCustomLayer(fieldId, layerId, { name });
    await this.loadCustomLayers(fieldId, true);
    await this.compareVM?.loadCustomLayers();
  };

  removeCustomLayer = async (fieldId: string, layerId: string) => {
    await deleteCustomLayer(fieldId, layerId);
    if (this.customLayerIdOf(this.selectedFieldView) === layerId) {
      this.setSelectedFieldView("border", "auto");
    }
    // The compare pane may be showing the very layer just deleted.
    const cvm = this.compareVM;
    if (cvm) {
      if (cvm.customLayerIdOf(cvm.viewMode) === layerId) cvm.setViewMode("border");
      await cvm.loadCustomLayers();
    }
    await this.loadCustomLayers(fieldId, true);
  };

  cameraStatistics: TCameraStatistics = {};
  private setCameraStatistics = (stats: TCameraStatistics) =>
    (this.cameraStatistics = stats);
  // What the heatmap source currently holds, so switching field/metric/scale
  // reloads while a plain re-render doesn't.
  private cameraLoadedKey: string | null = null;

  private loadCameraLayer = async (id: string) => {
    const metric = CAMERA_METRIC_BY_VIEW[this.selectedFieldView];
    if (!metric) return;
    const key = `${id}:${metric}:${this.yieldScale}`;
    if (this.cameraLoadedKey === key && this.heatmapSource.getFeatures().length > 0) return;
    const req = this.beginHeatmapRequest();
    try {
      const response = await fieldsService.getCameraHeatmapData(
        id,
        this.rootStore.seasonStore.currentSeasonId,
        metric,
        this.yieldScale
      );
      if (!this.isLatestHeatmap(req)) return;
      const format = this.heatmapSource.getFormat() ?? new GeoJSON();
      const features = format.readFeatures(response.features, optOptions) as Array<Feature<Geometry>>;
      features.forEach((feature) => {
        feature.setId(feature.get("id"));
        updateFeatureStyle(feature);
      });
      this.heatmapSource.clear(true);
      this.heatmapSource.addFeatures(features);
      this.cameraLoadedKey = key;
      this.customLoadedKey = null;
      const legend = { ...this.heatmapLegend };
      legend[id] = response.legend;
      this.setHeatmapLegend(legend);
      const statistics = { ...this.cameraStatistics };
      statistics[id] = response.stats;
      this.setCameraStatistics(statistics);
    } catch (e) {
      console.warn(e);
    } finally {
      this.endHeatmapRequest(req);
    }
  };

  // NDVI / soil brightness / soil moisture are single-date optical rasters from
  // the SAME Sentinel-2 scenes, so they share one date list and one overlay layer
  // (below). Only the backend image endpoint + legend differ per layer.
  isSpectralView = isSpectralView;

  get spectralLayer(): "ndvi" | "brightness" | "moisture" {
    return this.isSpectralView(this.selectedFieldView) ? this.selectedFieldView : "ndvi";
  }

  // ── Spectral overlay state (NDVI / brightness / moisture) ───
  // Historically named `ndvi*`; now shared across the three spectral layers.
  ndviLayer = new ImageLayer({ opacity: 0.85 });
  ndviDates: TNdviDateItem[] = [];
  ndviTotal = 0;
  ndviSelectedDate: string | null = null;
  ndviImageUrl: string | null = null;
  ndviLoading = false;
  private ndviThumbnailCache = new Map<string, string>(); // `${layer}:${fieldId}:${date}` → objectURL
  // Field id the current date list belongs to; lets us keep the dates when the
  // user only switches between spectral layers on the same field.
  private spectralFieldId: string | null = null;

  setNdviSelectedDate = (date: string) => {
    this.ndviSelectedDate = date;
    if (this.selectedFieldId != null) {
      this.loadNdviImage(String(this.selectedFieldId), date);
    }
  };

  loadNdviDates = async (fieldId: string, offset = 0) => {
    try {
      const res = await fieldsService.getNdviDates(
        fieldId,
        fieldsService.NDVI_DATE_PAGE_SIZE,
        offset
      );
      if (offset === 0) {
        this.ndviDates = res.dates;
      } else {
        this.ndviDates = [...this.ndviDates, ...res.dates];
      }
      this.ndviTotal = res.total;
      if (offset === 0) this.spectralFieldId = fieldId;

      // Auto-select latest date if none selected
      if (!this.ndviSelectedDate && res.dates.length > 0) {
        this.setNdviSelectedDate(res.dates[0].date);
      }
    } catch (e) {
      console.warn("Failed to load imagery dates", e);
    }
  };

  loadMoreNdviDates = () => {
    if (this.selectedFieldId == null) return;
    if (this.ndviDates.length >= this.ndviTotal) return;
    this.loadNdviDates(String(this.selectedFieldId), this.ndviDates.length);
  };

  private loadNdviImage = async (fieldId: string, date: string) => {
    this.ndviLoading = true;
    try {
      // Revoke previous URL
      if (this.ndviImageUrl) {
        URL.revokeObjectURL(this.ndviImageUrl);
        this.ndviImageUrl = null;
      }

      const objectUrl = await fieldsService.getSpectralImageUrl(
        fieldId,
        date,
        this.spectralLayer,
        false
      );
      this.ndviImageUrl = objectUrl;

      // Get field extent in EPSG:3857 for the image overlay
      const field = this.fieldsSource.getFeatureById(fieldId);
      if (!field) return;
      const extent = field.getGeometry()?.getExtent();
      if (!extent || isEmptyExtent(extent)) return;

      this.ndviLayer.setSource(
        new ImageStatic({
          url: objectUrl,
          imageExtent: extent,
        })
      );
      this.resetLayers();
    } catch (e) {
      console.warn("Failed to load imagery image", e);
    } finally {
      this.ndviLoading = false;
    }
  };

  getNdviThumbnail = async (
    fieldId: string,
    date: string,
    layer: "ndvi" | "brightness" | "moisture" = this.spectralLayer
  ): Promise<string> => {
    const key = `${layer}:${fieldId}:${date}`;
    const cached = this.ndviThumbnailCache.get(key);
    if (cached) return cached;
    const url = await fieldsService.getSpectralImageUrl(fieldId, date, layer, true);
    this.ndviThumbnailCache.set(key, url);
    return url;
  };

  private clearNdviState = () => {
    this.ndviDates = [];
    this.ndviTotal = 0;
    this.ndviSelectedDate = null;
    this.spectralFieldId = null;
    if (this.ndviImageUrl) {
      URL.revokeObjectURL(this.ndviImageUrl);
      this.ndviImageUrl = null;
    }
    this.ndviLayer.setSource(null);
  };

  // ── Productivity Map state ──────────────────────────────────
  productivityReady = false;
  productivityLegend: TProductivityLegendItem[] = [];
  productivityStats: TProductivityStats | null = null;
  productivityJob: TProductivityJob | null = null;
  productivityLoading = false;
  private productivityPollTimer: ReturnType<typeof setTimeout> | null = null;

  /** Load the stored productivity map for a field; if none, fetch job status
   * so the empty state can show progress / errors. Returns true once the map
   * was actually rendered. */
  /** Latest productivity load; a newer one (or a field change) wins. Not observable. */
  productivityReq = 0;

  /** Whether the productivity layer of `fieldId` is what the user is looking at. */
  private isShowingProductivity = (fieldId: string) =>
    this.selectedFieldView === "productivity" && String(this.selectedFieldId) === fieldId;

  loadProductivity = async (fieldId: string): Promise<boolean> => {
    const req = ++this.productivityReq;
    this.productivityLoading = true;
    this.productivitySource.clear(true);
    this.productivityReady = false;
    try {
      const res = await fieldsService.getProductivityMap(fieldId);
      // Field A's map arriving after the user opened field B must not paint B.
      if (req !== this.productivityReq || !this.isShowingProductivity(fieldId)) return false;
      if (res && res.geojson && res.legend) {
        const format = this.productivitySource.getFormat() ?? new GeoJSON();
        const features = format.readFeatures(res.geojson, optOptions) as Array<Feature<Geometry>>;
        features.forEach((feature) => updateFeatureStyle(feature));
        this.productivitySource.clear(true);
        this.productivitySource.addFeatures(features);
        this.productivityLegend = res.legend;
        this.productivityStats = res.stats ?? null;
        this.productivityReady = true;
        this.productivityJob = { status: "complete" };
        this.resetLayers();
        return true;
      } else {
        // No stored map yet — surface any prior/in-flight job state.
        await this.refreshProductivityStatus(fieldId);
        return false;
      }
    } catch (e) {
      console.warn("Failed to load productivity map", e);
      return false;
    } finally {
      if (req === this.productivityReq) this.productivityLoading = false;
    }
  };

  refreshProductivityStatus = async (fieldId: string) => {
    try {
      const job = await fieldsService.getProductivityStatus(fieldId);
      this.productivityJob = job;
      if (job.status === "running" || job.status === "pending") {
        this.pollProductivity(fieldId);
      }
    } catch (e) {
      console.warn("Failed to load productivity status", e);
    }
  };

  /** "Download historical data" → start (or reuse) generation, then poll. */
  startProductivityGeneration = async (fieldId: string, force = false) => {
    this.productivityJob = { status: "pending", progress: 0, stage: "Queued" };
    try {
      const res = await fieldsService.startProductivityGeneration(fieldId, force);
      this.productivityJob = res;
      if (res.status === "complete") {
        await this.loadProductivity(fieldId);
      } else {
        this.pollProductivity(fieldId);
      }
    } catch (e) {
      // Backend allows one productivity generation per user at a time. This is
      // not a failure — inform the user and restore any existing map (the guard
      // fires before the server discards the stored one, so it's still there).
      if ((e as { code?: string })?.code === "PRODUCTIVITY_IN_PROGRESS") {
        const lang = localizations[this.rootStore.settingsStore.language];
        toast.info(lang.prescription.busyProductivity);
        this.productivityJob = null;
        await this.loadProductivity(fieldId);
        return;
      }
      console.warn("Failed to start productivity generation", e);
      this.productivityJob = { status: "failed", error: "Failed to start generation" };
    }
  };

  /** Discard the stored map and rebuild it (e.g. after changing settings). */
  regenerateProductivity = async (fieldId: string) => {
    this.productivityReady = false;
    this.productivitySource.clear(true);
    this.productivityLegend = [];
    this.productivityStats = null;
    this.resetLayers();
    await this.startProductivityGeneration(fieldId, true);
  };

  private stopProductivityPoll = () => {
    if (this.productivityPollTimer) {
      clearTimeout(this.productivityPollTimer);
      this.productivityPollTimer = null;
    }
  };

  private pollProductivity = (fieldId: string) => {
    this.stopProductivityPoll();
    const tick = async () => {
      // The poll belongs to the field that started it. Once the user opens
      // another field or layer it stops; reopening restarts it from
      // refreshProductivityStatus. (It used to keep going and paint field A's
      // zones over field B when A's generation finished.)
      if (!this.isShowingProductivity(fieldId)) return;
      try {
        const job = await fieldsService.getProductivityStatus(fieldId);
        if (!this.isShowingProductivity(fieldId)) return;
        // Don't clobber a "complete" job we already rendered if the field was
        // switched away — but in the normal case keep the bar/stage live.
        if (!this.productivityReady) this.productivityJob = job;
        if (job.status === "complete") {
          // Job is done and the map row is committed server-side. If the fetch
          // doesn't yield it (transient error / stale proxy), keep polling and
          // retry rather than dead-ending on the empty-state button — the data
          // is definitely there.
          const loaded = await this.loadProductivity(fieldId);
          if (loaded) return;
        }
        if (job.status === "failed" || job.status === "none") return;
      } catch (e) {
        // transient — keep polling
      }
      this.productivityPollTimer = setTimeout(tick, 4000);
    };
    this.productivityPollTimer = setTimeout(tick, 4000);
  };

  private clearProductivityState = () => {
    this.stopProductivityPoll();
    this.productivitySource.clear(true);
    this.productivityReady = false;
    this.productivityLegend = [];
    this.productivityStats = null;
    this.productivityJob = null;
    this.productivityLoading = false;
  };

  // ── Compare mode ─────────────────────────────────────────
  isCompareMode = false;
  compareVM: CompareVM | null = null;
  private compareSyncActive = false;
  private viewSyncKeys: EventsKey[] = [];

  toggleCompareMode = () => {
    // Compare mode swaps the map layout — any pinned cell no longer applies.
    this.clearPinnedCell();
    if (this.isCompareMode) {
      // Exit compare mode
      this.compareSyncActive = false;
      this.teardownViewSync();
      this.linkedCursor.hide();
      this.compareVM?.dispose();
      this.compareVM = null;
      this.isCompareMode = false;
      setTimeout(() => this.map.updateSize(), 300);
    } else if (this.selectedFieldId != null) {
      // The evening's side-by-side preview holds the same split; compare
      // replaces it, but never under an apply/remove in flight.
      if (this.isYieldEvenOpen) {
        if (this.yieldEvenBusy) return;
        void this.closeYieldEven();
      }
      // Enter compare mode
      this.isCompareMode = true;
      const compareVM = new CompareVM(
        this.fieldsSource,
        this.rootStore.seasonStore,
        this.map.getView(),
        String(this.selectedFieldId)
      );
      this.compareVM = compareVM;
      this.setupViewSync(compareVM.map);
      // Delay to let DOM settle after flex layout change
      setTimeout(() => {
        this.map.updateSize();
        this.compareVM?.map.updateSize();
      }, 300);
    }
  };

  /** Two maps side by side: compare mode or the yield evening's preview. */
  get isSplitView(): boolean {
    return this.isCompareMode || this.yieldEvenPane != null;
  }

  /** The pane beside the main map, whichever of the two opened it. */
  private get sidePane(): CompareVM | null {
    return this.compareVM ?? this.yieldEvenPane;
  }

  private setupViewSync = (secondary: OpenLayersMap) => {
    const primaryView = this.map.getView();
    const compareView = secondary.getView();
    let syncing = false;
    this.compareSyncActive = true;

    const syncFrom = (source: typeof primaryView, target: typeof primaryView) => {
      if (!this.compareSyncActive || syncing) return;
      syncing = true;
      target.setCenter(source.getCenter());
      target.setResolution(source.getResolution());
      syncing = false;
    };

    this.viewSyncKeys = [
      primaryView.on("change:center", () => syncFrom(primaryView, compareView)),
      primaryView.on("change:resolution", () => syncFrom(primaryView, compareView)),
      compareView.on("change:center", () => syncFrom(compareView, primaryView)),
      compareView.on("change:resolution", () => syncFrom(compareView, primaryView)),
    ];
  };

  private teardownViewSync = () => {
    this.viewSyncKeys.forEach(unByKey);
    this.viewSyncKeys = [];
  };

  // ── Linked pointer ───────────────────────────────────────
  // The two panes share one view, so the same coordinate is at the same screen
  // position on both sides. Mirroring the pointer (and the hovered cell's
  // outline) is what lets a user say "that hexagon is this zone".

  /** True for the main map's data layers whose shapes are worth mirroring. */
  /**
   * Which layers' hovered feature is worth outlining on the other pane.
   *
   * Custom layers are excluded even though they render through `heatmapLayer`:
   * their features are dissolved zone polygons, so a prescription is often a
   * handful of huge scattered MultiPolygons and outlining one traces a third of
   * the field rather than the cell under the pointer. The ghost pointer still
   * syncs — only the shape outline is dropped.
   */
  isMirrorLayer = (layer: unknown): boolean => {
    if (this.isCustomView(this.selectedFieldView)) return false;
    return (
      layer === this.heatmapLayer ||
      layer === this.productivityLayer ||
      layer === this.tracksLayer
    );
  };

  /** Pointer moved over the MAIN map → draw its ghost on the compare pane. */
  syncCursorToCompare = (coord: Coordinate, origin?: Feature<Geometry> | null) =>
    this.sidePane?.linkedCursor.showCursor(coord, origin);

  hideCursorOnCompare = () => this.sidePane?.linkedCursor.hideCursor();

  /** Pointer moved over the COMPARE pane → draw its ghost on the main map. */
  syncCursorFromCompare = (coord: Coordinate, origin?: Feature<Geometry> | null) =>
    this.linkedCursor.showCursor(coord, origin);

  hideCursorOnMain = () => this.linkedCursor.hideCursor();

  selectedFieldId: string | number | null = null;
  private setSelectedFieldId = (v: string | number | null) => {
    this.selectedFieldId = v;
    // Landing on a field renders whatever layer is currently active — a real
    // map view even though the user selected nothing.
    if (v != null) this.trackMapView(this.selectedFieldView, "field", "field_change");
  };

  get selectedField() {
    return this.selectedFieldId != null
      ? this.fieldsSource.getFeatureById(this.selectedFieldId)
      : null;
  }

  // ── Pinned heatmap cell (click-to-locate) ──────────────────
  // Set when a user clicks a yield/camera cell; drives the map-anchored details
  // card + marker pin and the "open in Google Maps" share actions.
  pinnedCell: TPinnedCell | null = null;
  /** `cellFeature` is the clicked cell itself — its outline is mirrored onto the
   *  compare pane so the pinned area stays locatable on the other layer. */
  setPinnedCell = (cell: TPinnedCell, cellFeature?: Feature<Geometry> | null) => {
    this.pinnedCell = cell;
    // Same exclusion as isMirrorLayer, and it matters more here: a pinned
    // custom-layer zone would leave a third of the field outlined on the other
    // pane until dismissed. The pin marker itself still mirrors.
    const shape = this.isCustomView(this.selectedFieldView) ? null : cellFeature;
    this.sidePane?.linkedCursor.showPin(cell.coord3857, shape);
  };
  clearPinnedCell = () => {
    this.pinnedCell = null;
    this.sidePane?.linkedCursor.hidePin();
  };
  crops: TCrop[] = [];
  private setCrops = (crops: TCrop[]) => (this.crops = crops);
  getCropById = (id: string) => this.crops.find((crop) => crop.id === id);

  // ── Bulk selection ─────────────────────────────────────────
  selectionMode = false;
  selectedIds = new Set<string>();
  bulkSyncing = false;

  /** True when at least one selected field is linked to John Deere (externalId johndeere:*). */
  get selectedHasJohnDeere(): boolean {
    if (this.selectedIds.size === 0) return false;
    return this.fields.some((f) => {
      const id = f.getId();
      if (id == null || !this.selectedIds.has(String(id))) return false;
      const ext = (f.getProperties() as { externalId?: unknown }).externalId;
      return typeof ext === "string" && ext.startsWith("johndeere:");
    });
  }

  /** True when at least one selected field is linked to Cropwise (externalId cropwise:*). */
  get selectedHasCropwise(): boolean {
    if (this.selectedIds.size === 0) return false;
    return this.fields.some((f) => {
      const id = f.getId();
      if (id == null || !this.selectedIds.has(String(id))) return false;
      const ext = (f.getProperties() as { externalId?: unknown }).externalId;
      return typeof ext === "string" && ext.startsWith("cropwise:");
    });
  }

  toggleSelectionMode = () => {
    this.selectionMode = !this.selectionMode;
    if (!this.selectionMode) this.selectedIds.clear();
  };

  toggleSelected = (id: string | number) => {
    const key = String(id);
    if (this.selectedIds.has(key)) this.selectedIds.delete(key);
    else this.selectedIds.add(key);
  };

  selectAll = (ids: (string | number)[]) => {
    ids.forEach((id) => this.selectedIds.add(String(id)));
  };

  clearSelection = () => this.selectedIds.clear();

  constructor(
    private rootStore: RootStore,
    private navigate: NavigateFunction,
  ) {
    // `linkedCursor` is driven imperatively from pointer events at up to 60 fps
    // and nothing renders from it through React.
    makeAutoObservable(this, {
      trackDayCache: false,
      // Shallow on purpose: it holds the same ~150k track rows as
      // trackDayCache. Deep observation cloned every row into a MobX proxy, and
      // re-cloned ALL of them on each batch — on an iPhone that pushed Safari
      // past its memory limit and the tab was killed and reloaded (2026-10-03).
      // Readers only ever replace the array, never mutate a row.
      trackDevices: observable.ref,
      // Observable on its own; the reference never changes.
      trackTime: false,
      linkedCursor: false,
      lastTrackedMapView: false,
      // Whole yield-map responses (every hex) kept for instant preview/original
      // switching — never deep-observed. Nothing renders from the bookkeeping
      // fields either; the panel reads the small observable state instead.
      yieldEvenCache: false,
      yieldEvenInflight: false,
      yieldEvenOnScreen: false,
      yieldEvenFieldId: false,
      yieldEvenSeasonId: false,
      yieldEvenSeq: false,
      heatmapReq: false,
      productivityReq: false,
      yieldEvenGeneration: false,
      yieldEvenPrepareTimer: false,
      yieldEvenPrepareSeq: false,
      yieldEvenPendingAt: false,
      fieldsReq: false,
      harvestScope: false,
      disposers: false,
      started: false,
      disposed: false,
    });

    // Keep the draft counter in step with the source itself. OL's Draw adds
    // the finished feature AFTER dispatching drawend, so counting inside that
    // handler would be off by one.
    this.adjustSource.on(["addfeature", "removefeature"] as any, () => {
      this.adjustDraftCount = this.countDraftFeatures();
    });

    installTrackClip(this.tracksLayer, this.map, () =>
      this.selectedField?.getGeometry()
    );
  }

  /** Global reactions; registered by start(), removed by dispose(). */
  disposers: IReactionDisposer[] = [];
  started = false;
  disposed = false;

  /**
   * Side effects the constructor must not have: the field-list and selection
   * reactions. Called by FieldsScreenVMProvider on mount, paired with dispose().
   */
  start = () => {
    if (this.started || this.disposed) return;
    this.started = true;
    // Crops only name the rows (getCropById, with the field's own crop name as
    // the fallback), so the field list loads alongside them, not after.
    fieldsService.getCropsData()
      .then((crops) => {
        if (!this.disposed) this.setCrops(crops);
      })
      .catch((e) => console.warn("Failed to load crops", e));
    // Single reaction handles both initial load (fireImmediately) and
    // subsequent season/farm changes — avoids the duplicate GET /fields that
    // previously fired from an explicit loadFields() + fireImmediately.
    // Guard: skip until BOTH seasonId AND farmId have resolved. Waiting on
    // farmId matters — if we fired with farmId still undefined the first
    // request would omit the farm filter and pull the user's ENTIRE field
    // set (the exact load the farm scoping exists to avoid), then reload
    // scoped a moment later. Both stores populate on auth, so this is a
    // brief empty state, not a stuck one.
    this.disposers.push(reaction(
      () => [
        this.rootStore.seasonStore.currentSeasonId,
        this.rootStore.farmStore.currentFarmId,
      ],
      () => {
        if (
          !this.rootStore.seasonStore.currentSeasonId ||
          !this.rootStore.farmStore.currentFarmId
        ) {
          // No season/farm resolved yet: nothing to fetch — render the
          // empty state instead of spinning forever.
          this.setFields([]);
          this.fieldsSource.clear(true);
          this.setDefaultCenter();
          this.setLoading(false);
          return;
        }
        // Leave the avg-yield overview BEFORE loading: loadFields re-applies
        // it to the new features while it is active, and resetting only
        // afterwards left their names as "Name (12.3)".
        this.restoreCropView();
        this.loadFields().then(() => {
          this.setDefaultCenter();
          if (this.isCompareMode) this.toggleCompareMode();
          this.setSelectedFieldId(null);
          this.setSelectedFieldView("border", "auto");
          // Track days are per-season; keeping them would offer the user
          // days that belong to the season they just left.
          this.clearTrackState();
        });
      },
      { fireImmediately: true }
    ));

    this.disposers.push(reaction(
      () => [this.selectedFieldId, this.selectedFieldView],
      this.selectedFieldReaction
    ));
  };

  /**
   * Undo start() and release everything this screen holds. Idempotent.
   */
  dispose = () => {
    if (this.disposed) return;
    this.disposed = true;
    this.disposers.forEach((d) => d());
    this.disposers = [];
    // In-flight responses must not paint into a disposed VM.
    this.heatmapReq++;
    this.productivityReq++;
    this.trackRequestSeq++;
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    this.stopProductivityPoll();
    if (this.yieldEvenPrepareTimer) clearTimeout(this.yieldEvenPrepareTimer);
    this.yieldEvenPrepareTimer = null;
    this.compareVM?.dispose();
    this.compareVM = null;
    this.yieldEvenPane?.dispose();
    this.yieldEvenPane = null;
    this.clearNdviState();
    for (const url of this.ndviThumbnailCache.values()) URL.revokeObjectURL(url);
    this.ndviThumbnailCache.clear();
    this.trackDayCache.clear();
    this.tracksSource.clear(true);
    this.heatmapSource.clear(true);
    this.productivitySource.clear(true);
    this.map.setTarget(undefined);
  };


  /**
   * The field list and map, then the progress bars. Progress and the trial
   * rectangles load beside the list and neither holds it up: for a farm with
   * hundreds of fields the progress is the slow part, and it used to keep the
   * whole menu on its loading placeholder. Resolves once the list and the
   * trials are in; the bars fill in when their own request lands.
   */
  loadFields = async () => {
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    const farmId = this.rootStore.farmStore.currentFarmId;
    const req = ++this.fieldsReq;
    // A reload of the same season and farm (after a save, a delete, an
    // evening) keeps the bars on screen until the fresh numbers land; a new
    // season or farm starts from placeholders.
    const scope = `${seasonId ?? ""}|${farmId ?? ""}`;
    const previous =
      scope === this.harvestScope
        ? new Map(this.fields.map((f) => [String(f.getId()), f.get("harvest") as TFieldHarvest | null]))
        : null;
    this.harvestScope = scope;
    if (!previous) this.setHarvestLoading(true);
    this.setLoading(true);

    const progress = fieldsService.getHarvestProgress(seasonId, farmId).catch((e) => {
      console.warn("Failed to load harvest progress", e);
      return null;
    });
    // Load all trial rectangles for the overview
    const trials = this.loadAllTrials();

    const fields = await fieldsService.getFieldsData(seasonId, undefined, undefined, farmId, false);
    // A backend that predates the progress endpoint still sends `harvest`.
    const listHasHarvest = fields.features.features.some((f) => f.properties.harvest != null);
    if (!listHasHarvest && previous) {
      for (const f of fields.features.features) {
        const harvest = previous.get(String(f.properties.id)) ?? null;
        f.properties.harvest = harvest;
        f.properties.avg_yield = harvest?.avgYield ?? 0;
      }
    }
    const orderedFeatures = this.setupVector(this.fieldsSource, fields.features);
    this.setFields(sortFieldFeatures(orderedFeatures, this.sortBy, this.sortOrder));
    // Re-apply overall yield colors if that view is active
    if (this.overallMapView === "avgYield") {
      this.applyOverallMapColors();
    }
    this.setLoading(false);

    if (listHasHarvest) {
      this.setHarvestLoading(false);
    } else {
      void progress.then((p) => {
        // A newer load (or an unmount) owns the screen now.
        if (req !== this.fieldsReq || this.disposed) return;
        if (p) this.applyHarvestProgress(p);
        else this.setHarvestLoading(false);
      });
    }
    await trials;
  };

  /** Fill the list's progress bars once their request lands (see loadFields). */
  private applyHarvestProgress = (progress: Map<string, TFieldHarvest>) => {
    for (const f of this.fields) {
      const harvest = progress.get(String(f.getId())) ?? { ...fieldsService.NO_HARVEST };
      // Silent: only the list and the avg-yield overview read these, and both
      // are refreshed below.
      f.set("harvest", harvest, true);
      f.set("avg_yield", harvest.avgYield, true);
    }
    this.setFields(sortFieldFeatures(this.fields, this.sortBy, this.sortOrder));
    if (this.overallMapView === "avgYield") {
      this.applyOverallMapColors();
    }
    this.setHarvestLoading(false);
  };

  /** Load all trials for all fields in the current season (overview layer). */
  private loadAllTrials = async () => {
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (!seasonId) return;
    try {
      const allTrials = await fieldsService.getAllTrials(seasonId);
      this.trials = allTrials;
      this.renderTrialRectangles();
    } catch (e) {
      console.warn("Failed to load all trials", e);
    }
  };


  save = async ({ cropId, name, farmId, completed }: { cropId: string; name: string; farmId?: string; completed?: boolean }) => {
    if (!this.selectedFieldId || !this.isFieldEdit || !this.isPolygonReady) {
      return;
    }
    const feature = this.drawSource.getFeatureById(this.selectedFieldId);
    const props = feature?.getProperties();
    if (feature == null || props == null) return;
    feature.setProperties({ ...props, name, culture_id: cropId, completed });

    const field = this.fieldsSource.getFeatureById(this.selectedFieldId);
    field?.setProperties(feature.getProperties());
    field?.setGeometry(feature.getGeometry());

    this.handleCancel();
    const format = this.fieldsSource.getFormat() as GeoJSON;
    const featureObject = format.writeFeatureObject(field!, optOptions);
    await fieldsService
      // Pass the current season so the per-season completed flag targets the
      // right field_cultures row.
      .updateField(featureObject, farmId, this.rootStore.seasonStore.currentSeasonId)
      .then(this.loadFields)
      .catch((e) => {
        if (!this.handleYieldEvenedRefusal(e)) toast.error(e.message ?? e.toString());
      });
  };

  /**
   * Save only the field's dirt/moisture % (clean-weight reduction) for the
   * current season, then refresh the yield heatmap + field list so the shown
   * total weight and per-ha values reflect the new clean weight immediately.
   */
  updateFieldReduction = async (fieldId: string, reductionPercent: number) => {
    try {
      await fieldsService.updateFieldReduction(
        fieldId,
        reductionPercent,
        this.rootStore.seasonStore.currentSeasonId,
      );
      // The cached heatmap feature must refetch (backend cache was invalidated).
      this.heatmapSource.clear(true);
      await this.loadHeatmapIfNeeded(fieldId);
      await this.loadFields();
    } catch (e: any) {
      toast.error(e?.message ?? e?.toString?.() ?? "Failed to update");
    }
  };

  importBoundaryFeatures = async (
    features: GeoJsonFeature[],
    season_id?: string,
    cropId?: string
  ) => {
    this.setLoading(true);
    // The per-field tally (imported / already present / failed) is the caller's
    // to report, so it has to survive the refresh chain below.
    return importBoundaryFeatures(features, season_id, cropId, this.rootStore.farmStore.currentFarmId)
      .then(async (result) => {
        await this.loadFields();
        // Fit the camera to the fields so freshly imported boundaries are visible
        // immediately — loadFields() refreshes the source + list but leaves the
        // view where it was, which for a new account is the default Europe zoom.
        this.setDefaultCenter();
        return result;
      })
      .finally(() => this.setLoading(false));
  }

  create = async ({ cropId, name }: { cropId: string; name: string }) => {
    if (!this.isPolygonReady) return;
    const feature = this.drawSource.getFeatures()[0];
    const props = feature?.getProperties();
    if (feature == null || props == null) return;
    feature.setProperties({ ...props, name, culture_id: cropId });

    this.handleCancel();
    const format = this.fieldsSource.getFormat() as GeoJSON;
    const featureObject = format.writeFeatureObject(feature, optOptions);
    await fieldsService
      .createField(
        featureObject,
        this.rootStore.seasonStore.currentSeasonId,
        this.rootStore.farmStore.currentFarmId
      )
      .then(this.loadFields)
      .catch((e) => {
        console.error(e);
        toast.error(e.message ?? e.toString())
      });
  };

  selectField = (fieldId: string | number) => {
    const feature = this.fieldsSource.getFeatureById(fieldId);
    if (feature === null) return;
    const extent = feature.getGeometry()?.getExtent();
    const padding = [20, 20, 20, 20];
    if (extent != null && !isEmptyExtent(extent)) {
      this.map.getView().fit(extent, { padding });
    }

    // When clicking a field while in avgYield overview, switch to per-field yield view
    const wasAvgYield = this.overallMapView === "avgYield";
    if (wasAvgYield) {
      // Restore crop colors and original names before entering per-field mode
      this.restoreCropView();
      // Left untracked on purpose: setSelectedFieldId (below) emits a single
      // field_change event carrying this view, so tracking here would double it.
      this.selectedFieldView = "yield";
    }

    this.setSelectedFieldId(fieldId);
    this.fields.forEach((f) => {
      updateFeatureStyle(f, {
        selected: f.getId() === this.selectedFieldId,
        border: true,
      });
    });

    // Update compare panel for the new field
    if (this.isCompareMode && this.compareVM) {
      this.compareVM.updateFieldId(String(fieldId));
    }

    const q = new URLSearchParams(window.location.search);
    q.set("fid", fieldId.toString());
    this.navigate(window.location.pathname + "?" + q.toString(), { replace: true });
  };

  handleFieldUpdate = (id: string | number) => {
    this.selectField(id);

    const feature = this.fieldsSource.getFeatureById(id);
    if (feature === null) {
      console.error("Failed to find feature for field: ", id);
      return;
    }

    this.drawSource.clear(true);
    this.drawSource.addFeature(feature.clone());
    this.map.addInteraction(this.modifyInteraction);
    this.drawSource.getFeatures().forEach((feature) => {
      feature.setId(feature.get("id"));
    });

    this.setIsFieldEdit(true);
    this.setIsPolygonReady(true);
    this.resetLayers();
  };

  handleFieldCreate = () => {
    this.drawSource.clear(true);
    this.map.addInteraction(this.drawInteraction);

    this.drawInteraction.once("drawend", (event) => {
      this.map.removeInteraction(this.drawInteraction);
      this.map.addInteraction(this.modifyInteraction);
      this.setIsPolygonReady(true);
    });

    this.setIsFieldEdit(true);
    this.setIsPolygonReady(false);
    this.setSelectedFieldId(null);
    this.resetLayers();
  };

  handleCancel = () => {
    this.drawInteraction.abortDrawing();

    this.map.removeInteraction(this.drawInteraction);
    this.map.removeInteraction(this.modifyInteraction);

    this.drawSource.clear(true);

    // Clean up any active trial drawing, then reload all trials for the overview
    if (this.isTrialDrawing) this.cancelTrialDraw();
    this.loadAllTrials();

    this.setIsFieldEdit(false);
    this.setIsPolygonReady(false);
    this.resetLayers();
    this.fields.forEach((f) => {
      updateFeatureStyle(f, {
        selected: f.getId() === this.selectedFieldId,
        border: true,
      });
    });
  };

  // ── Yield correction zones ─────────────────────────────────
  // Some fields carry technical artifacts — long straight high-value stripes
  // along combine passes — that inflate the total and distort the legend. The
  // user selects those areas (lasso and/or by clicking hexes) and either
  // scales them down by a % or flattens them to the field average. The
  // correction lives on the backend and is applied on read; here we only
  // author the geometry.
  //
  // Zones are drawn ONLY while this edit mode is active — outside it the map
  // simply shows corrected values.

  private adjustSource = new VectorSource({ wrapX: false });
  adjustLayer = new VectorLayer({
    source: this.adjustSource,
    style: (feature) =>
      feature.get("saved")
        ? new Style({
            stroke: new Stroke({ color: "#FF5252", width: 2, lineDash: [8, 4] }),
            fill: new Fill({ color: "rgba(255, 82, 82, 0.12)" }),
          })
        : new Style({
            stroke: new Stroke({ color: "#FF5252", width: 2 }),
            fill: new Fill({ color: "rgba(255, 82, 82, 0.35)" }),
          }),
  });
  private adjustDrawInteraction: Draw | null = null;

  isYieldAdjustEdit = false;
  /** "hex" = click hexagons to toggle them; "lasso" = drag to draw a shape. */
  adjustTool: "hex" | "lasso" = "hex";
  adjustMode: fieldsService.TYieldAdjustmentMode = "percent";
  adjustPercent = 30;
  adjustName = "";
  adjustSaving = false;
  yieldAdjustments: fieldsService.TYieldAdjustment[] = [];
  /** Bumped whenever the draft selection changes, so observers re-render. */
  adjustDraftCount = 0;

  setAdjustMode = (mode: fieldsService.TYieldAdjustmentMode) => {
    this.adjustMode = mode;
  };
  /** Negative = raise the zone's yield instead of cutting it. */
  setAdjustPercent = (p: number) => {
    this.adjustPercent = Math.min(100, Math.max(-100, p));
  };
  setAdjustName = (n: string) => {
    this.adjustName = n;
  };

  toggleYieldAdjustEdit = async () => {
    if (this.isYieldAdjustEdit) {
      this.exitYieldAdjustEdit();
      return;
    }
    // Zones are locked while the yield is evened (the server refuses them too).
    // The toolbar button stays tappable so a phone user gets this reason.
    if (this.selectedFieldEvened) {
      toast.info(this.fieldsLang.yieldEvenZonesLocked);
      return;
    }
    // The two yield tools share the panel slot and the map.
    if (this.isYieldEvenOpen) {
      if (this.yieldEvenBusy) return;
      void this.closeYieldEven();
    }
    this.isYieldAdjustEdit = true;
    this.adjustTool = "hex";
    this.clearAdjustDraft();
    await this.loadYieldAdjustments();
    this.resetLayers();
  };

  exitYieldAdjustEdit = () => {
    this.stopAdjustLasso();
    this.adjustSource.clear();
    this.isYieldAdjustEdit = false;
    this.adjustTool = "hex";
    this.adjustName = "";
    this.resetLayers();
  };

  loadYieldAdjustments = async () => {
    const fieldId = this.selectedFieldId;
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (fieldId == null || !seasonId) return;
    try {
      this.setYieldAdjustments(
        await fieldsService.listYieldAdjustments(String(fieldId), seasonId)
      );
      this.renderSavedAdjustments();
    } catch (e) {
      console.warn(e);
    }
  };

  private setYieldAdjustments = (list: fieldsService.TYieldAdjustment[]) => {
    this.yieldAdjustments = list;
  };

  /** Draw the saved zones as dashed outlines (edit mode only). */
  private renderSavedAdjustments = () => {
    this.adjustSource
      .getFeatures()
      .filter((f) => f.get("saved"))
      .forEach((f) => this.adjustSource.removeFeature(f));
    if (!this.isYieldAdjustEdit) return;
    const format = new GeoJSON();
    for (const adj of this.yieldAdjustments) {
      try {
        const feature = format.readFeature(adj.geometry, optOptions) as Feature<Geometry>;
        feature.setId(`adjust-saved-${adj.id}`);
        feature.set("saved", true);
        this.adjustSource.addFeature(feature);
      } catch {
        // A zone we can't render must not break the rest of the panel.
      }
    }
  };

  // ── Draft selection ──
  // Both selection methods produce plain polygons in the draft source; on save
  // they become one MultiPolygon. The backend clips that against the field
  // boundary, which also merges adjacent pieces into a clean outline.

  private draftId = (suffix: string) => `adjust-draft-${suffix}`;

  /** Add or remove a clicked yield hexagon from the draft selection. */
  toggleHexInDraft = (hexFeature: Feature<Geometry>) => {
    const geom = hexFeature.getGeometry();
    if (!geom || geom.getType() !== "Polygon") return;
    const id = this.draftId(`hex-${String(hexFeature.getId())}`);
    const existing = this.adjustSource.getFeatureById(id);
    if (existing) {
      this.adjustSource.removeFeature(existing);
    } else {
      const clone = new Feature((geom as Polygon).clone());
      clone.setId(id);
      this.adjustSource.addFeature(clone);
    }
  };

  /** Remove a draft shape the user clicked on (works for lasso shapes too). */
  removeDraftFeature = (feature: Feature<Geometry>) => {
    if (feature.get("saved")) return false;
    this.adjustSource.removeFeature(feature);
    return true;
  };

  private countDraftFeatures = () =>
    this.adjustSource.getFeatures().filter((f) => !f.get("saved")).length;

  clearAdjustDraft = () => {
    this.adjustSource
      .getFeatures()
      .filter((f) => !f.get("saved"))
      .forEach((f) => this.adjustSource.removeFeature(f));
  };

  startAdjustLasso = () => {
    if (this.adjustDrawInteraction) return;
    this.adjustTool = "lasso";
    // freehand: drag a shape around the artifact rather than clicking vertices.
    this.adjustDrawInteraction = new Draw({
      source: this.adjustSource,
      type: "Polygon",
      freehand: true,
    });
    this.adjustDrawInteraction.on("drawend", (event) => {
      (event.feature as Feature).setId(this.draftId(`lasso-${Date.now()}`));
      // One shape per press of the lasso button, then back to hex clicking so
      // a stray drag can't keep adding shapes.
      this.stopAdjustLasso();
    });
    this.map.addInteraction(this.adjustDrawInteraction);
  };

  stopAdjustLasso = () => {
    if (this.adjustDrawInteraction) {
      this.adjustDrawInteraction.abortDrawing?.();
      this.map.removeInteraction(this.adjustDrawInteraction);
      this.adjustDrawInteraction = null;
    }
    this.adjustTool = "hex";
  };

  saveYieldAdjustment = async () => {
    const fieldId = this.selectedFieldId;
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (fieldId == null || !seasonId) return;

    const drafts = this.adjustSource.getFeatures().filter((f) => !f.get("saved"));
    if (drafts.length === 0) return;

    const coordinates: number[][][][] = [];
    for (const feature of drafts) {
      const geom = feature.getGeometry();
      if (!geom || geom.getType() !== "Polygon") continue;
      const rings = (geom as Polygon)
        .getCoordinates()
        .map((ring) => ring.map((c) => transform(c, "EPSG:3857", "EPSG:4326")));
      if (rings.length > 0 && rings[0].length >= 3) coordinates.push(rings);
    }
    if (coordinates.length === 0) return;

    this.setAdjustSaving(true);
    try {
      await fieldsService.createYieldAdjustment(String(fieldId), {
        seasonId,
        name: this.adjustName.trim() || null,
        mode: this.adjustMode,
        reductionPercent: this.adjustMode === "percent" ? this.adjustPercent : null,
        geometry: { type: "MultiPolygon", coordinates },
      });
      this.clearAdjustDraft();
      this.adjustName = "";
      await this.refreshAfterAdjustmentChange();
    } catch (e: any) {
      if (!this.handleYieldEvenedRefusal(e)) toast.error(e?.message ?? "Failed to save correction");
    } finally {
      this.setAdjustSaving(false);
    }
  };

  updateYieldAdjustment = async (
    adjustmentId: string,
    data: { name?: string | null; mode?: fieldsService.TYieldAdjustmentMode; reductionPercent?: number | null }
  ) => {
    const fieldId = this.selectedFieldId;
    if (fieldId == null) return;
    this.setAdjustSaving(true);
    try {
      await fieldsService.updateYieldAdjustment(String(fieldId), adjustmentId, data);
      await this.refreshAfterAdjustmentChange();
    } catch (e: any) {
      if (!this.handleYieldEvenedRefusal(e)) toast.error(e?.message ?? "Failed to update correction");
    } finally {
      this.setAdjustSaving(false);
    }
  };

  deleteYieldAdjustment = async (adjustmentId: string) => {
    const fieldId = this.selectedFieldId;
    if (fieldId == null) return;
    this.setAdjustSaving(true);
    try {
      await fieldsService.deleteYieldAdjustment(String(fieldId), adjustmentId);
      await this.refreshAfterAdjustmentChange();
    } catch (e: any) {
      if (!this.handleYieldEvenedRefusal(e)) toast.error(e?.message ?? "Failed to delete correction");
    } finally {
      this.setAdjustSaving(false);
    }
  };

  private setAdjustSaving = (v: boolean) => {
    this.adjustSaving = v;
  };

  /**
   * The backend recomputed the corrected yield and dropped its cache, so the
   * heatmap has to refetch. Same sequence updateFieldReduction uses.
   */
  private refreshAfterAdjustmentChange = async () => {
    const fieldId = this.selectedFieldId;
    if (fieldId == null) return;
    await this.loadYieldAdjustments();
    this.heatmapSource.clear(true);
    await this.loadHeatmapIfNeeded(String(fieldId));
    await this.loadFields();
    this.resetLayers();
  };

  // ── Yield evening ──────────────────────────────────────────
  // "Even out yield", by machine: the backend splits the field into areas
  // harvested by one machine within one half hour and moves each area up or
  // down as a whole so neighbouring machines agree, keeping the pattern inside
  // each area and the field total. It needs this season's machine tracks read
  // first — a background scan the panel starts and follows (prepare). The
  // panel previews the result through the same heatmap plumbing as the stored
  // map (so the legend and stats card follow it) and applies it. Applying also
  // completes and freezes the field, and locks correction zones.
  //
  // While the panel is open it owns the heatmap source. Each map it can show
  // is one GET /yield response (see TYieldEvenKey), cached so that "Show
  // original" flips instantly instead of refetching.

  isYieldEvenOpen = false;
  yieldEvenShowOriginal = false;
  /** The map the panel asked for is being fetched. */
  yieldEvenLoading = false;
  /** Apply or remove in flight — the panel's controls are disabled. */
  yieldEvenBusy: "apply" | "remove" | null = null;
  /** Evening blocks of the responses fetched for the open panel. */
  yieldEvenBlocks: Partial<Record<TYieldEvenKey, fieldsService.THeatmapEvening>> = {};
  /** Views whose fetch failed or came back as a different view. */
  yieldEvenFailed: Partial<Record<TYieldEvenKey, boolean>> = {};
  /**
   * How far reading this season's machine tracks is, as the server last said.
   * Null until it answers, and again while asking anew (stale preview).
   */
  yieldEvenPrepare: fieldsService.TEveningPrepare | null = null;
  /** A prepare call is in flight. */
  yieldEvenPreparing = false;
  /** The last prepare call itself failed (network, server error). */
  yieldEvenPrepareError = false;

  // Not observable (see makeAutoObservable).
  yieldEvenCache = new Map<string, fieldsService.THeatmapResponse>();
  yieldEvenInflight = new Map<string, Promise<fieldsService.THeatmapResponse | null>>();
  /** What the panel last put on screen; null when unknown. */
  yieldEvenOnScreen: TYieldEvenKey | null = null;
  /** The field the panel was opened for. */
  yieldEvenFieldId: string | null = null;
  /** The season it was opened in — a poll must never start another season's scan. */
  yieldEvenSeasonId: string | null = null;
  /** Bumped per map sync — only the latest sync may draw. */
  yieldEvenSeq = 0;
  /** Bumped when the cache is dropped — older responses are discarded. */
  yieldEvenGeneration = 0;
  /** The next prepare poll while the scan runs. */
  yieldEvenPrepareTimer: ReturnType<typeof setTimeout> | null = null;
  /** Bumped per prepare call and when polling stops — only the latest answer counts. */
  yieldEvenPrepareSeq = 0;
  /** When a preview last came back "pending" (ms since epoch). */
  yieldEvenPendingAt = 0;

  /**
   * Desktop: the result in its own pane beside the main map, which then shows
   * the original. Null on a phone, where one map flips with "Show original".
   */
  yieldEvenPane: CompareVM | null = null;

  /** The selected field's yield is evened this season (season-scoped, like `completed`). */
  get selectedFieldEvened(): boolean {
    const id = this.selectedFieldId;
    if (id == null) return false;
    // Read through `fields` (observable), not the OL source, so the locks and
    // the chip update as soon as loadFields lands.
    const field = this.fields.find((f) => String(f.getId()) === String(id));
    return Boolean(field?.get("yield_evened"));
  }

  /** The evening applied to the selected field, as the latest yield response reported it. */
  get selectedFieldEvening(): fieldsService.TEveningActive | null {
    const id = this.selectedFieldId;
    if (id == null) return null;
    return this.heatmapEvening[String(id)]?.active ?? null;
  }

  /** Evened according to either the field list or the yield response. */
  get yieldEvenIsApplied(): boolean {
    return this.selectedFieldEvened || this.selectedFieldEvening != null;
  }

  /**
   * The result the panel describes: the applied evening when it was made by
   * machine, otherwise the by-machine preview — which replaces an evening made
   * with the earlier method.
   */
  get yieldEvenResultKey(): TYieldEvenKey {
    return this.selectedFieldEvening?.strength === "machine" ? "stored" : "machine";
  }

  /**
   * The result can be shown: the applied evening, or a preview whose machine
   * tracks are read. Until then the main map keeps the stored map and the
   * desktop split stays closed.
   */
  get yieldEvenResultReady(): boolean {
    return this.yieldEvenResultKey === "stored" || this.yieldEvenPrepare?.status === "ready";
  }

  /** The map on the main pane while the panel is open. */
  get yieldEvenDisplayKey(): TYieldEvenKey {
    if (!this.yieldEvenResultReady) return "stored";
    return this.yieldEvenPane || this.yieldEvenShowOriginal ? "none" : this.yieldEvenResultKey;
  }

  /** What the result changes. Only a by-machine summary — an earlier method's is not shown. */
  get yieldEvenSummary(): fieldsService.TEveningSummary | null {
    if (this.yieldEvenResultKey === "stored") {
      const summary = this.selectedFieldEvening?.summary;
      return summary && fieldsService.isMachineEveningSummary(summary) ? summary : null;
    }
    if (!this.yieldEvenResultReady) return null;
    return this.yieldEvenBlocks.machine?.preview?.summary ?? null;
  }

  get yieldEvenNoop(): fieldsService.TEveningNoop | null {
    if (this.yieldEvenResultKey === "stored" || !this.yieldEvenResultReady) return null;
    return this.yieldEvenBlocks.machine?.preview?.noop ?? null;
  }

  /** The map on screen or the result described could not be loaded. */
  get yieldEvenFailedToLoad(): boolean {
    return Boolean(
      this.yieldEvenFailed[this.yieldEvenResultKey] ||
        this.yieldEvenFailed[this.yieldEvenDisplayKey]
    );
  }

  get yieldEvenCanApply(): boolean {
    if (this.yieldEvenBusy || this.yieldEvenLoading) return false;
    if (this.yieldEvenResultKey !== "machine" || !this.yieldEvenResultReady) return false;
    const preview = this.yieldEvenBlocks.machine?.preview;
    return Boolean(preview && preview.status === "ready" && !preview.noop && preview.fingerprint);
  }

  /** The fields copy in the user's language, for toasts raised here. */
  private get fieldsLang() {
    return localizations[this.rootStore.settingsStore.language].fields;
  }

  /** Header button: open the panel, or close it when it is already open. */
  toggleYieldEven = () => {
    if (this.isYieldEvenOpen) void this.closeYieldEven();
    else this.openYieldEven();
  };

  openYieldEven = () => {
    const id = this.selectedFieldId;
    if (this.isYieldEvenOpen || id == null || this.selectedFieldView !== "yield") return;
    // The two yield tools share the panel slot and the map.
    if (this.isYieldAdjustEdit) this.exitYieldAdjustEdit();
    this.clearYieldEvenCache();
    this.yieldEvenFieldId = String(id);
    this.yieldEvenSeasonId = this.rootStore.seasonStore.currentSeasonId ?? null;
    this.yieldEvenShowOriginal = false;
    this.yieldEvenBusy = null;
    this.yieldEvenPrepare = null;
    this.yieldEvenPrepareError = false;
    // The stored map is on screen, unless its fetch is still running.
    this.yieldEvenOnScreen = this.heatmapLoading ? null : "stored";
    this.isYieldEvenOpen = true;
    if (this.yieldEvenResultReady) {
      // Evened by machine already: what is saved needs no tracks read.
      void this.syncYieldEvenMap();
    } else {
      // The preview needs this season's machine tracks. The map splits once
      // they are read (see applyYieldEvenPrepare).
      void this.prepareYieldEven();
    }
  };

  toggleYieldEvenOriginal = () => {
    if (this.yieldEvenBusy) return;
    this.yieldEvenShowOriginal = !this.yieldEvenShowOriginal;
    void this.syncYieldEvenMap();
  };

  /**
   * "Try again": restart a scan that failed, ask again after a failed call,
   * or reload a map that could not be loaded.
   */
  retryYieldEvenPrepare = () => {
    if (!this.isYieldEvenOpen || this.yieldEvenBusy || this.yieldEvenPreparing) return;
    if (this.yieldEvenResultReady) {
      // Failed fetches are not cached, so a sync fetches them again.
      void this.syncYieldEvenMap();
      return;
    }
    void this.prepareYieldEven(this.yieldEvenPrepare?.status === "failed");
  };

  /** Cancel: close the panel and put the stored map back. */
  closeYieldEven = async () => {
    if (!this.isYieldEvenOpen || this.yieldEvenBusy) return;
    const fieldId = this.yieldEvenFieldId;
    const onScreen = this.yieldEvenOnScreen;
    // With no evening applied, the map without one IS the stored map.
    const replacement =
      fieldId == null
        ? undefined
        : this.yieldEvenCache.get(this.yieldEvenCacheKey(fieldId, "stored")) ??
          (this.yieldEvenIsApplied
            ? undefined
            : this.yieldEvenCache.get(this.yieldEvenCacheKey(fieldId, "none")));
    this.resetYieldEven();
    if (fieldId == null || onScreen === "stored") return;
    if (replacement) {
      this.renderHeatmapResponse(fieldId, replacement);
      return;
    }
    this.heatmapSource.clear(true);
    await this.loadHeatmapIfNeeded(fieldId);
  };

  applyYieldEven = async () => {
    const fieldId = this.yieldEvenFieldId;
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (!this.isYieldEvenOpen || fieldId == null || !seasonId) return;
    if (!this.yieldEvenCanApply) return;
    const preview = this.yieldEvenBlocks.machine?.preview;
    if (!preview) return;
    this.setYieldEvenBusy("apply");
    try {
      await fieldsService.applyYieldEvening(fieldId, seasonId, preview.fingerprint);
    } catch (e) {
      if (this.yieldEvenFieldId === fieldId) this.setYieldEvenBusy(null);
      this.handleYieldEvenError(e);
      return;
    }
    toast.success(this.fieldsLang.yieldEvenApplied);
    if (this.yieldEvenFieldId === fieldId && this.selectedFieldView === "yield") {
      this.resetYieldEven();
      await this.refreshAfterYieldEvenChange(fieldId);
    } else {
      // The user moved on while it saved; only the list needs the news.
      await this.loadFields();
    }
  };

  removeYieldEven = async () => {
    const fieldId = this.yieldEvenFieldId;
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (!this.isYieldEvenOpen || fieldId == null || !seasonId || this.yieldEvenBusy) return;
    this.setYieldEvenBusy("remove");
    try {
      await fieldsService.removeYieldEvening(fieldId, seasonId);
    } catch (e) {
      if (this.yieldEvenFieldId === fieldId) this.setYieldEvenBusy(null);
      this.handleYieldEvenError(e);
      return;
    }
    toast.success(this.fieldsLang.yieldEvenRemoved);
    if (this.yieldEvenFieldId !== fieldId) {
      await this.loadFields();
      return;
    }
    // The measured map is the stored one again. Keep the panel open on it so
    // the user can preview the by-machine evening or close. That preview needs
    // the tracks read — already done when an earlier method's evening was
    // removed, not yet when a by-machine one was.
    this.markYieldEvenRemoved(fieldId);
    if (this.yieldEvenPrepare?.status === "ready") void this.syncYieldEvenMap();
    else void this.prepareYieldEven();
    // Stay busy until the list stops calling the field evened, so Remove can't
    // be pressed twice.
    await this.loadFields();
    if (this.yieldEvenFieldId === fieldId) this.setYieldEvenBusy(null);
  };

  /** Field or view changed under the open panel: drop it without a restore. */
  private abandonYieldEven = () => {
    const fieldId = this.yieldEvenFieldId;
    const onScreen = this.yieldEvenOnScreen;
    this.resetYieldEven();
    if (fieldId == null || onScreen === "stored") return;
    // A preview went through the shared legend/stats. Don't let its numbers
    // stand in for the stored map's when this field is shown again.
    const legend = { ...this.heatmapLegend };
    delete legend[fieldId];
    this.setHeatmapLegend(legend);
    const statistics = { ...this.heatmapStatistics };
    delete statistics[fieldId];
    this.setHeatmapStatistics(statistics);
  };

  /** Close the panel and forget its responses. Leaves the main map as it is. */
  private resetYieldEven = () => {
    this.isYieldEvenOpen = false;
    this.yieldEvenShowOriginal = false;
    this.yieldEvenBusy = null;
    this.yieldEvenLoading = false;
    this.yieldEvenOnScreen = null;
    this.yieldEvenFieldId = null;
    this.yieldEvenSeasonId = null;
    this.yieldEvenSeq++;
    // Every way out of the panel (close, apply, field/view change) ends here:
    // stop following the scan.
    this.stopYieldEvenPrepare();
    this.yieldEvenPrepare = null;
    this.yieldEvenPrepareError = false;
    this.clearYieldEvenCache();
    this.closeYieldEvenPane();
  };

  /** Desktop: split the map, the result on the right (see yieldEvenPane). */
  private openYieldEvenPane = (fieldId: string) => {
    // Compare mode already holds the split; a phone has no room for two maps.
    if (this.yieldEvenPane || this.isCompareMode || window.innerWidth <= MOBILE_BREAKPOINT) return;
    // The layout changes under a pinned cell's card.
    this.clearPinnedCell();
    const pane = new CompareVM(
      this.fieldsSource,
      this.rootStore.seasonStore,
      this.map.getView(),
      fieldId,
      "machine"
    );
    this.yieldEvenPane = pane;
    this.setupViewSync(pane.map);
    // Delay to let DOM settle after flex layout change
    setTimeout(() => {
      this.map.updateSize();
      pane.map.updateSize();
    }, 300);
  };

  private closeYieldEvenPane = () => {
    const pane = this.yieldEvenPane;
    if (!pane) return;
    this.compareSyncActive = false;
    this.teardownViewSync();
    this.linkedCursor.hide();
    pane.dispose();
    this.yieldEvenPane = null;
    setTimeout(() => this.map.updateSize(), 300);
  };

  private clearYieldEvenCache = () => {
    this.yieldEvenGeneration++;
    this.yieldEvenCache.clear();
    this.yieldEvenInflight.clear();
    this.yieldEvenBlocks = {};
    this.yieldEvenFailed = {};
  };

  private markYieldEvenRemoved = (fieldId: string) => {
    this.setHeatmapEvening({ ...this.heatmapEvening, [fieldId]: { view: "stored", active: null } });
    this.clearYieldEvenCache();
    this.yieldEvenOnScreen = null;
    this.yieldEvenShowOriginal = true;
  };

  private setYieldEvenLoading = (v: boolean) => (this.yieldEvenLoading = v);
  private setYieldEvenBusy = (v: "apply" | "remove" | null) => (this.yieldEvenBusy = v);
  private setYieldEvenBlock = (key: TYieldEvenKey, evening: fieldsService.THeatmapEvening) =>
    (this.yieldEvenBlocks = { ...this.yieldEvenBlocks, [key]: evening });
  private setYieldEvenFailed = (key: TYieldEvenKey, failed: boolean) =>
    (this.yieldEvenFailed = { ...this.yieldEvenFailed, [key]: failed });

  /** One cached response per field, season, scale and evening view. */
  private yieldEvenCacheKey = (fieldId: string, key: TYieldEvenKey) =>
    `${fieldId}|${this.rootStore.seasonStore.currentSeasonId ?? ""}|${this.yieldScale}|${key}`;

  /**
   * Fetch one evening view of the yield map, or reuse it. Resolves null when
   * the fetch failed or returned another view (an older backend ignores the
   * param). Those are not cached, so asking again retries.
   */
  private loadYieldEvenResponse = (
    fieldId: string,
    key: TYieldEvenKey
  ): Promise<fieldsService.THeatmapResponse | null> => {
    const cacheKey = this.yieldEvenCacheKey(fieldId, key);
    const cached = this.yieldEvenCache.get(cacheKey);
    if (cached) return Promise.resolve(cached);
    const pending = this.yieldEvenInflight.get(cacheKey);
    if (pending) return pending;
    const generation = this.yieldEvenGeneration;
    if (this.yieldEvenFailed[key]) this.setYieldEvenFailed(key, false);
    const request = fieldsService
      .getHeatmapData(
        fieldId,
        this.rootStore.seasonStore.currentSeasonId,
        this.yieldScale,
        key === "stored" ? undefined : key
      )
      .then((response) => {
        // Dropped meanwhile (panel closed, scale changed, data changed).
        if (generation !== this.yieldEvenGeneration) return null;
        this.yieldEvenInflight.delete(cacheKey);
        if (!isEveningView(key, response)) {
          this.setYieldEvenFailed(key, true);
          return null;
        }
        if (response.evening?.preview?.status === "pending") {
          // Not a preview: the season's machine tracks are not read (any
          // more) and these hexes are the map without evening. Read them again.
          this.yieldEvenTracksPending();
          return null;
        }
        this.yieldEvenCache.set(cacheKey, response);
        this.setYieldEvenBlock(key, fieldsService.heatmapEveningOf(response));
        return response;
      })
      .catch((e) => {
        // getHeatmapData already turns HTTP errors into an empty map; this is
        // the unexpected rest. Never leave a stuck in-flight entry behind.
        console.warn(e);
        if (generation !== this.yieldEvenGeneration) return null;
        this.yieldEvenInflight.delete(cacheKey);
        this.setYieldEvenFailed(key, true);
        return null;
      });
    this.yieldEvenInflight.set(cacheKey, request);
    return request;
  };

  /**
   * Make the map show what the panel asks for, then warm the counterpart so
   * "Show original" flips instantly and the summary is there while the
   * original is on screen.
   *
   * Side by side, the counterpart is the pane's map instead: the main map
   * shows the original and the pane the result, both colored on the
   * original's legend so a color means the same yield on both sides.
   *
   * Until the result can be shown (the machine tracks are still being read)
   * the split stays closed and the main map shows the stored map.
   */
  private syncYieldEvenMap = async () => {
    const fieldId = this.yieldEvenFieldId;
    if (!this.isYieldEvenOpen || fieldId == null) return;
    const seq = ++this.yieldEvenSeq;
    const ready = this.yieldEvenResultReady;
    if (ready) this.openYieldEvenPane(fieldId);
    else this.closeYieldEvenPane();
    const displayKey = this.yieldEvenDisplayKey;
    const counterpart: TYieldEvenKey | null = !ready
      ? null
      : displayKey === "none"
        ? this.yieldEvenResultKey
        : "none";
    const pane = this.yieldEvenPane;
    // Both maps are fetched at once.
    const paneRequest = pane && counterpart ? this.loadYieldEvenResponse(fieldId, counterpart) : null;
    if (pane && counterpart) {
      pane.setLoading(!this.yieldEvenCache.has(this.yieldEvenCacheKey(fieldId, counterpart)));
    }

    if (this.yieldEvenOnScreen !== displayKey) {
      const cached = this.yieldEvenCache.get(this.yieldEvenCacheKey(fieldId, displayKey));
      this.setYieldEvenLoading(!cached);
      const response = cached ?? (await this.loadYieldEvenResponse(fieldId, displayKey));
      // A newer sync owns the map and the spinner.
      if (seq !== this.yieldEvenSeq) return;
      this.setYieldEvenLoading(false);
      if (response) {
        // A pinned cell's value belongs to the map it was read from.
        this.clearPinnedCell();
        this.renderHeatmapResponse(fieldId, response, pane ? response.legend : undefined);
        this.yieldEvenOnScreen = displayKey;
      }
    } else {
      this.setYieldEvenLoading(false);
    }

    if (pane && paneRequest) {
      const response = await paneRequest;
      if (seq !== this.yieldEvenSeq || pane !== this.yieldEvenPane) return;
      pane.setLoading(false);
      if (!response) return;
      const original = this.yieldEvenCache.get(this.yieldEvenCacheKey(fieldId, "none"));
      pane.showEveningResult(response, original?.legend ?? [], "machine");
      return;
    }
    if (counterpart && counterpart !== this.yieldEvenOnScreen) {
      void this.loadYieldEvenResponse(fieldId, counterpart);
    }
  };

  // ── Reading the season's machine tracks (prepare) ──

  /**
   * Have this season's machine tracks read for the by-machine preview, then
   * follow the scan (see applyYieldEvenPrepare). `retry` restarts a failed one.
   */
  private prepareYieldEven = async (retry = false) => {
    const fieldId = this.yieldEvenFieldId;
    const seasonId = this.yieldEvenSeasonId;
    if (!this.isYieldEvenOpen || fieldId == null || !seasonId) return;
    this.stopYieldEvenPrepare();
    // The season changed under the panel (its reaction closes the panel once
    // the new field list is in). Don't start a scan of a season nobody asked for.
    if (seasonId !== this.rootStore.seasonStore.currentSeasonId) return;
    const seq = this.yieldEvenPrepareSeq;
    this.yieldEvenPreparing = true;
    let state: fieldsService.TEveningPrepare | null = null;
    try {
      state = await fieldsService.prepareYieldEvening(fieldId, seasonId, retry);
    } catch (e) {
      console.warn(e);
    }
    // Closed, moved to another field or view, or a newer call owns the answer.
    if (seq !== this.yieldEvenPrepareSeq) return;
    this.applyYieldEvenPrepare(state);
  };

  /** Take in prepare's answer; null when the call itself failed. */
  private applyYieldEvenPrepare = (state: fieldsService.TEveningPrepare | null) => {
    this.yieldEvenPreparing = false;
    if (!state) {
      // A blip while the scan runs: keep following it.
      if (this.yieldEvenPrepare?.status === "scanning") this.scheduleYieldEvenPrepare();
      else this.yieldEvenPrepareError = true;
      return;
    }
    this.yieldEvenPrepareError = false;
    this.yieldEvenPrepare = state;
    // Poll while it scans — unless nothing needs the tracks any more (the
    // field turned out to be evened by machine already).
    if (state.status === "scanning" && this.yieldEvenResultKey === "machine") {
      this.scheduleYieldEvenPrepare();
    }
    // Ready: split the map and load the preview. Otherwise keep the stored
    // map on the main pane.
    void this.syncYieldEvenMap();
  };

  private scheduleYieldEvenPrepare = () => {
    if (this.yieldEvenPrepareTimer) clearTimeout(this.yieldEvenPrepareTimer);
    this.yieldEvenPrepareTimer = setTimeout(() => {
      this.yieldEvenPrepareTimer = null;
      void this.prepareYieldEven();
    }, YIELD_EVEN_PREPARE_POLL_MS);
  };

  /**
   * Stop following the scan: no next poll, and an answer still in flight is
   * ignored. Also called when the map unmounts (leaving the screen).
   */
  stopYieldEvenPrepare = () => {
    this.yieldEvenPrepareSeq++;
    if (this.yieldEvenPrepareTimer) {
      clearTimeout(this.yieldEvenPrepareTimer);
      this.yieldEvenPrepareTimer = null;
    }
    this.yieldEvenPreparing = false;
  };

  /**
   * A preview came back "pending": the scan was raced or the tracks went
   * stale. Read them again and follow the scan.
   */
  private yieldEvenTracksPending = () => {
    if (!this.isYieldEvenOpen) return;
    this.yieldEvenPrepare = null;
    // Not straight away twice in a row, so a server that keeps answering
    // "ready" to prepare and "pending" to the preview is asked every 5 s
    // instead of in a tight loop.
    const now = Date.now();
    const again = now - this.yieldEvenPendingAt < 2 * YIELD_EVEN_PREPARE_POLL_MS;
    this.yieldEvenPendingAt = now;
    if (!again) {
      void this.prepareYieldEven();
      return;
    }
    this.scheduleYieldEvenPrepare();
    // Meanwhile the main map goes back to the stored one.
    void this.syncYieldEvenMap();
  };

  /** The stored map and the field list changed server-side. */
  private refreshAfterYieldEvenChange = async (fieldId: string) => {
    this.heatmapSource.clear(true);
    await this.loadHeatmapIfNeeded(fieldId);
    await this.loadFields();
    this.resetLayers();
  };

  /** The preview is stale: read the tracks again (they may be too) and reload it. */
  private reloadYieldEvenPreview = () => {
    if (!this.isYieldEvenOpen) return;
    this.clearYieldEvenCache();
    this.yieldEvenOnScreen = null;
    if (this.yieldEvenResultKey === "stored") {
      void this.syncYieldEvenMap();
      return;
    }
    this.yieldEvenPrepare = null;
    void this.prepareYieldEven();
  };

  /** One toast per failed apply/remove, translated where the code is known. */
  private handleYieldEvenError = (e: unknown) => {
    const t = this.fieldsLang;
    switch (fieldsService.apiErrorCode(e)) {
      case "YIELD_CHANGED":
        // The preview is stale; refetch it so the user applies what they see.
        toast.warning(t.yieldEvenErrorChanged);
        this.reloadYieldEvenPreview();
        return;
      case "YIELD_EVEN_NOOP":
        toast.info(t.yieldEvenErrorNoop);
        this.reloadYieldEvenPreview();
        return;
      case "YIELD_BUSY":
        toast.info(t.yieldEvenErrorBusy);
        return;
      default:
        toast.error((e as Error | null)?.message || t.yieldEvenErrorGeneric);
    }
  };

  /**
   * 409 YIELD_EVENED: the server refused a change the evening locks
   * (un-completing the field, editing correction zones). Our copy of the field
   * predates the evening — say why once and reload it. False for other errors.
   */
  private handleYieldEvenedRefusal = (e: unknown): boolean => {
    if (fieldsService.apiErrorCode(e) !== "YIELD_EVENED") return false;
    toast.error(this.fieldsLang.yieldEvenErrorEvened);
    if (this.isYieldAdjustEdit) this.exitYieldAdjustEdit();
    const id = this.selectedFieldId;
    if (id != null && this.selectedFieldView === "yield" && !this.isYieldEvenOpen) {
      this.heatmapSource.clear(true);
      void this.loadHeatmapIfNeeded(String(id));
    }
    void this.loadFields();
    return true;
  };

  // ── Field Trials ───────────────────────────────────────────

  private trialDrawSource = new VectorSource({ wrapX: false });
  private trialDrawInteraction: Draw | null = null;
  private trialModifyInteraction: Modify | null = null;
  trialDrawLayer = new VectorLayer({
    source: this.trialDrawSource,
    style: (feature) => {
      const geomType = feature.getGeometry()?.getType();
      if (geomType === "Point") {
        return new Style({
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({ color: "#ffffff" }),
            stroke: new Stroke({ color: "#68ad18", width: 2 }),
          }),
        });
      }
      if (geomType === "Polygon") {
        return new Style({
          stroke: new Stroke({ color: "#FF9800", width: 2 }),
          fill: new Fill({ color: "rgba(255, 152, 0, 0.15)" }),
        });
      }
      return new Style({
        stroke: new Stroke({ color: "#FF9800", width: 2, lineDash: [6, 4] }),
      });
    },
  });

  private trialRectSource = new VectorSource({ wrapX: false });
  trialRectLayer = new VectorLayer({
    source: this.trialRectSource,
    style: (feature, resolution) => {
      const name = feature.get("trialName") ?? "";
      // resolution is meters/pixel — lower = more zoomed in
      // Show label only when zoomed in close (resolution < 10 ≈ zoom ~15)
      const showLabel = name && resolution < 10;
      // Scale font size: bigger when closer
      const fontSize = resolution < 3 ? 13 : resolution < 5 ? 11 : 10;
      return new Style({
        stroke: new Stroke({ color: "#68ad18", width: 2, lineDash: [8, 4] }),
        fill: new Fill({ color: "rgba(104, 173, 24, 0.15)" }),
        text: showLabel
          ? new OlText({
              text: name,
              font: `600 ${fontSize}px Nunito Sans, sans-serif`,
              fill: new Fill({ color: "#494c5b" }),
              stroke: new Stroke({ color: "#ffffff", width: 3 }),
              overflow: true,
            })
          : undefined,
      });
    },
  });

  isTrialDrawing = false;
  /** Active draw tool: "line" (center-line + width), "polygon" (freehand), or "rectangle" (box). */
  trialDrawTool: "line" | "polygon" | "rectangle" | null = null;
  trialPointA: { lng: number; lat: number } | null = null;
  trialPointB: { lng: number; lat: number } | null = null;
  /** Outer ring [lng,lat] for polygon/rectangle trials (WGS84). */
  trialGeometry4326: number[][] | null = null;
  trialWidthMeters = 6;
  trialName = "";
  trials: TFieldTrial[] = [];
  /** True once a shape has been drawn (line points placed, or polygon/box completed). */
  trialPointsPlaced = false;
  /** Set when reshaping a saved trial (PATCH instead of create). */
  editingTrialId: string | null = null;

  setTrialWidth = (w: number) => {
    this.trialWidthMeters = w;
    if (this.trialDrawTool === "line" && this.trialPointA && this.trialPointB) {
      this.updateTrialPreview();
    }
  };

  setTrialName = (n: string) => {
    this.trialName = n;
  };

  handleAddTrial = (tool: "line" | "polygon" | "rectangle" = "line") => {
    this.clearPinnedCell();
    this.isTrialDrawing = true;
    this.trialDrawTool = tool;
    this.trialPointA = null;
    this.trialPointB = null;
    this.trialGeometry4326 = null;
    this.trialPointsPlaced = false;
    this.trialName = "";
    this.trialWidthMeters = 6;
    this.editingTrialId = null;
    this.trialDrawSource.clear();

    if (tool === "line") {
      this.trialDrawInteraction = new Draw({
        source: new VectorSource(), // don't add to trialDrawSource directly
        type: "LineString",
        maxPoints: 2,
      });

      this.trialDrawInteraction.on("drawend", (event) => {
        const line = event.feature.getGeometry() as LineString;
        const coords = line.getCoordinates();
        if (coords.length >= 2) {
          const a4326 = transform(coords[0], "EPSG:3857", "EPSG:4326");
          const b4326 = transform(coords[1], "EPSG:3857", "EPSG:4326");
          this.trialPointA = { lng: a4326[0], lat: a4326[1] };
          this.trialPointB = { lng: b4326[0], lat: b4326[1] };
          this.trialPointsPlaced = true;

          // Add point markers for dragging
          const featureA = new Feature(new Point(coords[0]));
          featureA.setId("trial-point-a");
          const featureB = new Feature(new Point(coords[1]));
          featureB.setId("trial-point-b");
          this.trialDrawSource.addFeature(featureA);
          this.trialDrawSource.addFeature(featureB);

          this.updateTrialPreview();
          this.attachLineModify();
        }
        // Remove draw interaction after points are placed
        if (this.trialDrawInteraction) {
          this.map.removeInteraction(this.trialDrawInteraction);
        }
      });
    } else {
      // Freehand polygon or rectangle/box — drawn straight into trialDrawSource.
      this.trialDrawInteraction = new Draw({
        source: this.trialDrawSource,
        type: tool === "rectangle" ? "Circle" : "Polygon",
        geometryFunction: tool === "rectangle" ? createBox() : undefined,
      });

      this.trialDrawInteraction.on("drawend", (event) => {
        const feature = event.feature as Feature;
        feature.setId("trial-shape");
        this.captureTrialPolygon(feature);
        this.trialPointsPlaced = true;
        if (this.trialDrawInteraction) {
          this.map.removeInteraction(this.trialDrawInteraction);
        }
        this.attachPolygonModify(tool);
      });
    }

    this.map.addInteraction(this.trialDrawInteraction);
    this.resetLayers();
  };

  /** Re-enter draw/modify mode for a saved trial so its shape can be edited. */
  editTrialShape = (trial: TFieldTrial) => {
    this.clearPinnedCell();
    this.isTrialDrawing = true;
    this.editingTrialId = trial.id;
    this.trialName = trial.name;
    this.trialPointsPlaced = true;
    this.trialPointA = trial.pointA ?? null;
    this.trialPointB = trial.pointB ?? null;
    this.trialWidthMeters = trial.widthMeters ?? 6;
    this.trialGeometry4326 = null;
    this.trialDrawSource.clear();

    if (trial.shape === "line_width" && trial.pointA && trial.pointB) {
      this.trialDrawTool = "line";
      const aCoord = transform([trial.pointA.lng, trial.pointA.lat], "EPSG:4326", "EPSG:3857");
      const bCoord = transform([trial.pointB.lng, trial.pointB.lat], "EPSG:4326", "EPSG:3857");
      const featureA = new Feature(new Point(aCoord));
      featureA.setId("trial-point-a");
      const featureB = new Feature(new Point(bCoord));
      featureB.setId("trial-point-b");
      this.trialDrawSource.addFeature(featureA);
      this.trialDrawSource.addFeature(featureB);
      this.updateTrialPreview();
      this.attachLineModify();
    } else {
      this.trialDrawTool = "polygon";
      const ring3857 = trial.geometry.coordinates[0].map((c) =>
        transform(c, "EPSG:4326", "EPSG:3857")
      );
      const feature = new Feature(new Polygon([ring3857]));
      feature.setId("trial-shape");
      this.trialDrawSource.addFeature(feature);
      this.trialGeometry4326 = trial.geometry.coordinates[0].map((c) => [c[0], c[1]]);
      this.attachPolygonModify("polygon");
    }

    this.resetLayers();
  };

  private attachLineModify = () => {
    this.trialModifyInteraction = new Modify({
      source: this.trialDrawSource,
      deleteCondition: () => false,
    });
    this.trialModifyInteraction.on("modifyend", () => {
      const fA = this.trialDrawSource.getFeatureById("trial-point-a");
      const fB = this.trialDrawSource.getFeatureById("trial-point-b");
      if (fA && fB) {
        const cA = (fA.getGeometry() as Point).getCoordinates();
        const cB = (fB.getGeometry() as Point).getCoordinates();
        const a = transform(cA, "EPSG:3857", "EPSG:4326");
        const b = transform(cB, "EPSG:3857", "EPSG:4326");
        this.trialPointA = { lng: a[0], lat: a[1] };
        this.trialPointB = { lng: b[0], lat: b[1] };
        this.updateTrialPreview();
      }
    });
    this.map.addInteraction(this.trialModifyInteraction);
  };

  private attachPolygonModify = (tool: "polygon" | "rectangle") => {
    this.trialModifyInteraction = new Modify({
      source: this.trialDrawSource,
      // Freehand polygons allow deleting a vertex by double-click; a box keeps its 4 corners.
      deleteCondition: tool === "polygon" ? doubleClick : () => false,
    });
    this.trialModifyInteraction.on("modifyend", () => {
      this.captureTrialPolygon(this.trialDrawSource.getFeatureById("trial-shape"));
    });
    this.map.addInteraction(this.trialModifyInteraction);
  };

  private captureTrialPolygon = (feature: Feature<Geometry> | null | undefined) => {
    if (!feature) return;
    const geom = feature.getGeometry() as Polygon;
    const ring3857 = geom.getCoordinates()[0];
    this.trialGeometry4326 = ring3857.map((c) => transform(c, "EPSG:3857", "EPSG:4326"));
  };

  private updateTrialPreview = () => {
    if (!this.trialPointA || !this.trialPointB) return;
    const ring = computeTrialRectangle(
      this.trialPointA,
      this.trialPointB,
      this.trialWidthMeters
    );
    // Remove old preview polygon
    const existing = this.trialDrawSource.getFeatureById("trial-preview");
    if (existing) this.trialDrawSource.removeFeature(existing);

    const coords3857 = ring.map((c) => transform(c, "EPSG:4326", "EPSG:3857"));
    const polygon = new Polygon([coords3857]);
    const feature = new Feature(polygon);
    feature.setId("trial-preview");
    this.trialDrawSource.addFeature(feature);
  };

  saveTrial = async () => {
    if (!this.selectedFieldId) return;
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (!seasonId) return;
    const fieldId = String(this.selectedFieldId);
    const name = this.trialName || "Untitled trial";

    try {
      if (this.trialDrawTool === "line") {
        if (!this.trialPointA || !this.trialPointB) return;
        if (this.editingTrialId) {
          await fieldsService.updateTrial(fieldId, this.editingTrialId, {
            name,
            shape: "line_width",
            pointA: this.trialPointA,
            pointB: this.trialPointB,
            widthMeters: this.trialWidthMeters,
          });
        } else {
          await fieldsService.createTrial(fieldId, {
            shape: "line_width",
            name,
            seasonId,
            pointA: this.trialPointA,
            pointB: this.trialPointB,
            widthMeters: this.trialWidthMeters,
          });
        }
      } else {
        if (!this.trialGeometry4326 || this.trialGeometry4326.length < 3) return;
        const geometry: GeoJsonPolygon = {
          type: "Polygon",
          coordinates: [this.trialGeometry4326],
        };
        if (this.editingTrialId) {
          await fieldsService.updateTrial(fieldId, this.editingTrialId, {
            name,
            shape: "polygon",
            geometry,
          });
        } else {
          await fieldsService.createTrial(fieldId, {
            shape: "polygon",
            name,
            seasonId,
            geometry,
          });
        }
      }
      this.cancelTrialDraw();
      await this.loadAllTrials();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save trial");
    }
  };

  cancelTrialDraw = () => {
    if (this.trialDrawInteraction) {
      this.trialDrawInteraction.abortDrawing?.();
      this.map.removeInteraction(this.trialDrawInteraction);
      this.trialDrawInteraction = null;
    }
    if (this.trialModifyInteraction) {
      this.map.removeInteraction(this.trialModifyInteraction);
      this.trialModifyInteraction = null;
    }
    this.trialDrawSource.clear();
    this.isTrialDrawing = false;
    this.trialDrawTool = null;
    this.trialPointA = null;
    this.trialPointB = null;
    this.trialGeometry4326 = null;
    this.trialPointsPlaced = false;
    this.trialName = "";
    this.trialWidthMeters = 6;
    this.editingTrialId = null;
    this.resetLayers();
  };

  private renderTrialRectangles = () => {
    this.trialRectSource.clear();
    for (const trial of this.trials) {
      const geom = trial.geometry as any;
      if (!geom || !geom.coordinates) continue;
      const coords3857 = geom.coordinates[0].map((c: number[]) =>
        transform(c, "EPSG:4326", "EPSG:3857")
      );
      const polygon = new Polygon([coords3857]);
      const feature = new Feature(polygon);
      feature.setId(trial.id);
      feature.set("trialName", trial.name);
      this.trialRectSource.addFeature(feature);
    }
  };

  renameTrial = async (trialId: string, newName: string) => {
    if (!this.selectedFieldId || !newName.trim()) return;
    try {
      await fieldsService.updateTrial(String(this.selectedFieldId), trialId, { name: newName.trim() });
      await this.loadAllTrials();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to rename trial");
    }
  };

  deleteTrialById = async (trialId: string) => {
    if (!this.selectedFieldId) return;
    try {
      await fieldsService.deleteTrial(String(this.selectedFieldId), trialId);
      await this.loadAllTrials();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to delete trial");
    }
  };

  deleteField = (id: string | number) =>
    fieldsService.deleteField(id).then(this.loadFields).then(this.handleCancel);

  deleteAllFields = () =>
    fieldsService.deleteAllFields().then(this.loadFields).then(this.handleCancel);

  // ── Bulk actions ───────────────────────────────────────────
  bulkChangeCrop = async (cropId: string) => {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) return;
    try {
      await fieldsService.bulkChangeCrop(
        ids,
        cropId,
        this.rootStore.seasonStore.currentSeasonId
      );
      this.clearSelection();
      await this.loadFields();
    } catch (e: any) {
      toast.error(e.message ?? e.toString());
    }
  };

  // Bulk-complete the selected fields: freezes them (stops automatic John Deere
  // sync + device-calibration recomputes) for the current season. Backend fires
  // the same final-sync-on-complete as the per-field toggle.
  bulkComplete = async () => {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) return;
    try {
      await fieldsService.bulkSetCompleted(
        ids,
        true,
        this.rootStore.seasonStore.currentSeasonId
      );
      this.clearSelection();
      this.selectionMode = false;
      await this.loadFields();
    } catch (e: any) {
      toast.error(e.message ?? e.toString());
    }
  };

  bulkDelete = async () => {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) return;
    try {
      await fieldsService.bulkDeleteFields(ids);
      this.clearSelection();
      this.selectionMode = false;
      await this.loadFields();
      this.handleCancel();
    } catch (e: any) {
      toast.error(e.message ?? e.toString());
    }
  };

  bulkExport = async (format: "kml" | "shp") => {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) return;
    try {
      await fieldsService.bulkExportFields(
        ids,
        format,
        this.rootStore.seasonStore.currentSeasonId
      );
    } catch (e: any) {
      toast.error(e.message ?? e.toString());
    }
  };

  bulkMoveToFarm = async (farmId: string) => {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0 || !farmId) return;
    try {
      await moveFieldsToFarm(ids, farmId);
      this.clearSelection();
      this.selectionMode = false;
      // Fields moved out of the current farm vanish from the view; reload to reflect it.
      await this.loadFields();
    } catch (e: any) {
      toast.error(e.message ?? e.toString());
    }
  };

  // Manual bulk sync of the selected fields to John Deere. Pushes every selected
  // field regardless of completion (explicit user action); the backend reports a
  // per-field status which we summarize in a toast.
  bulkSyncToDeere = async () => {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0 || this.bulkSyncing) return;
    this.bulkSyncing = true;
    const toastId = toast.loading(`Syncing ${ids.length} field(s) to John Deere…`);
    try {
      const res = await fieldsService.bulkSyncYieldToDeere(
        ids,
        this.rootStore.seasonStore.currentSeasonId
      );
      const labels: Record<string, string> = {
        synced: "synced",
        "skipped-unchanged": "unchanged",
        "no-data": "no yield data",
        "not-linked": "not linked to John Deere",
        "not-connected": "not connected",
        failed: "failed",
      };
      const message =
        Object.entries(res.summary)
          .map(([status, n]) => `${n} ${labels[status] ?? status}`)
          .join(" · ") || "Nothing to sync";
      const hasProblem =
        (res.summary.failed ?? 0) > 0 || (res.summary["not-connected"] ?? 0) > 0;
      toast.update(toastId, {
        render: message,
        type: hasProblem ? "warning" : "success",
        isLoading: false,
        autoClose: 5000,
      });
      this.clearSelection();
      this.selectionMode = false;
    } catch (e: any) {
      // The api client already surfaced an error toast; just clear the spinner.
      toast.dismiss(toastId);
    } finally {
      this.bulkSyncing = false;
    }
  };

  /**
   * Push the selected fields' yield maps to Cropwise.
   *
   * Loops client-side rather than calling a bulk endpoint: there isn't one, and
   * adding a second server-side batch runner for a handful of fields isn't worth
   * it. Sequential on purpose — Cropwise's rate limits are undocumented, so
   * firing N uploads in parallel is a gratuitous risk.
   *
   * A per-field rejection does NOT abort the run; it's tallied like any other
   * status, because "unsupported" (Cropwise refused the write) is an expected
   * outcome here until Syngenta confirms the yield_map API.
   */
  bulkSyncToCropwise = async () => {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0 || this.bulkSyncing) return;
    // Yield is per (field, season); with no season resolved there is nothing to
    // push, and the backend would reject each call anyway.
    const seasonId = this.rootStore.seasonStore.currentSeasonId;
    if (!seasonId) {
      toast.warning("Select a season before syncing yields to Cropwise.");
      return;
    }
    this.bulkSyncing = true;
    const toastId = toast.loading(`Syncing ${ids.length} field(s) to Cropwise…`);
    const summary: Record<string, number> = {};
    let firstUnsupportedDetail: string | undefined;

    try {
      for (const fieldId of ids) {
        try {
          const r = await syncYieldToCropwise({ fieldId, seasonId });
          summary[r.status] = (summary[r.status] ?? 0) + 1;
          if (r.status === "unsupported" && !firstUnsupportedDetail) {
            firstUnsupportedDetail = r.details;
          }
        } catch {
          // The api client already toasted; keep going so one bad field can't
          // strand the rest of the batch.
          summary.failed = (summary.failed ?? 0) + 1;
        }
      }

      const labels: Record<string, string> = {
        synced: "synced",
        "skipped-unchanged": "unchanged",
        "no-data": "no yield data",
        "not-linked": "not linked to Cropwise",
        "not-connected": "not connected",
        unsupported: "rejected by Cropwise",
        failed: "failed",
      };
      const message =
        Object.entries(summary)
          .map(([status, n]) => `${n} ${labels[status] ?? status}`)
          .join(" · ") || "Nothing to sync";
      const hasProblem =
        (summary.failed ?? 0) > 0 ||
        (summary["not-connected"] ?? 0) > 0 ||
        (summary.unsupported ?? 0) > 0;

      toast.update(toastId, {
        render: message,
        type: hasProblem ? "warning" : "success",
        isLoading: false,
        autoClose: 5000,
      });
      if (firstUnsupportedDetail) {
        console.warn("[Cropwise] yield_map upload rejected:", firstUnsupportedDetail);
      }
      this.clearSelection();
      this.selectionMode = false;
    } finally {
      this.bulkSyncing = false;
    }
  };
}

export const isRecentlyUpdated = (stats: TFieldStats | null): boolean => {
  return !!stats;
};

/**
 * A field feature's area in hectares: the server's area, else the geodesic
 * area of its polygon, else null (no or malformed geometry). Format it with
 * useFormatArea.
 */
export const getFieldAreaHectares = (field?: Feature<Geometry> | null): number | null => {
  if (field == null) return null;
  const m2 = field.get("area_m2");
  if (typeof m2 === "number" && m2 > 0) return m2 / 10000;
  const geometry = field.getGeometry();
  if (geometry == null) return null;
  try {
    return getArea(geometry) / 10000;
  } catch {
    // Malformed polygon (e.g. missing outer ring) breaks ol/sphere getArea
    return null;
  }
};
